/**
 * YearWheelPage - Årshjulet
 * 
 * Visar återkommande underhållsuppgifter i ett årshjul-format
 * med möjlighet att:
 * - Se uppgifter månadsvis eller i listvy
 * - Markera uppgifter som utförda
 * - Lägga till egna uppgifter
 * - Aktivera/inaktivera uppgifter
 */

import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  useYearWheelStore,
  useYearWheelHydrated,
  RecurringTask, 
  TaskFrequency, 
  TaskDeadline,
  PrepArea,
  frequencyLabels,
  deadlineLabels,
  DEFAULT_TASK_DEADLINE,
} from '../stores/yearwheel-store';
import { useGamificationStore, POINT_VALUES } from '../stores/gamification-store';
import { useAuthStore } from '../stores/auth-store';
import { useFamilyStore } from '../stores/family-store';
import { useShoppingListStore } from '../stores/shopping-list-store';
import { CATEGORY_LABELS, type InventoryCategory } from '../stores/inventory-store';
import PremiumRequiredModal from '../components/PremiumRequiredModal';
import CSVImportModal from '../components/CSVImportModal';
import {
  Droplets,
  Utensils,
  Zap,
  Heart,
  Radio,
  Home,
  Car,
  Phone,
  Shield,
  AlertTriangle,
  Plus,
  Check,
  X,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CalendarDays,
  ShoppingCart,
  Sprout,
  Circle,
  Copy,
  Grid3x3,
  Rows3,
  FileUp,
  FileDown,
  RotateCcw,
  Minus,
  type LucideIcon,
} from 'lucide-react';

// Områdes-ikoner och färger i prioritetsordning (anpassade för dark mode)
const areaConfig: Record<PrepArea, { icon: LucideIcon; color: string; iconColor: string; name: string }> = {
  water: { icon: Droplets, color: 'bg-blue-900/30 text-blue-300 border-blue-700', iconColor: 'text-blue-400', name: 'Vatten' },
  food: { icon: Utensils, color: 'bg-amber-900/30 text-amber-300 border-amber-700', iconColor: 'text-amber-400', name: 'Mat' },
  shelter: { icon: Home, color: 'bg-stone-700/30 text-stone-300 border-stone-600', iconColor: 'text-stone-400', name: 'Skydd' },
  health: { icon: Heart, color: 'bg-red-900/30 text-red-300 border-red-700', iconColor: 'text-red-400', name: 'Hälsa' },
  communication: { icon: Phone, color: 'bg-indigo-900/30 text-indigo-300 border-indigo-700', iconColor: 'text-indigo-400', name: 'Kommunikation' },
  information: { icon: Radio, color: 'bg-purple-900/30 text-purple-300 border-purple-700', iconColor: 'text-purple-400', name: 'Information' },
  energy: { icon: Zap, color: 'bg-yellow-900/30 text-yellow-300 border-yellow-700', iconColor: 'text-yellow-400', name: 'Energi' },
  safety: { icon: Shield, color: 'bg-slate-700/30 text-slate-300 border-slate-600', iconColor: 'text-slate-400', name: 'Säkerhet' },
  transport: { icon: Car, color: 'bg-green-900/30 text-green-300 border-green-700', iconColor: 'text-green-400', name: 'Transport' },
  shopping: { icon: ShoppingCart, color: 'bg-teal-900/30 text-teal-300 border-teal-700', iconColor: 'text-teal-400', name: 'Inköp' },
  gardening: { icon: Sprout, color: 'bg-lime-900/30 text-lime-300 border-lime-700', iconColor: 'text-lime-400', name: 'Odla' },
  other: { icon: Circle, color: 'bg-gray-700/30 text-gray-300 border-gray-600', iconColor: 'text-gray-400', name: 'Övrigt' },
};

const months = [
  'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
  'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'
];

function getFrequencyIntervalMonths(frequency: TaskFrequency): number {
  switch (frequency) {
    case 'monthly':
      return 1;
    case 'quarterly':
      return 3;
    case 'biannual':
      return 6;
    case 'annual':
      return 12;
    default:
      return 1;
  }
}

function getDeadlineDay(deadlineType: TaskDeadline, year: number, month: number): number {
  if (deadlineType === 'lastDay') {
    return new Date(year, month + 1, 0).getDate();
  }

  switch (deadlineType) {
    case 'day21':
      return 21;
    case 'day14':
      return 14;
    case 'day7':
      return 7;
    default:
      return new Date(year, month + 1, 0).getDate();
  }
}

function getOccurrenceDueDate(year: number, month: number, deadlineType: TaskDeadline): Date {
  return new Date(year, month, getDeadlineDay(deadlineType, year, month));
}

function formatDateToYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

type TaskOccurrence = {
  task: RecurringTask;
  month: number;
  year: number;
  nextDue: string;
};

function compareTasksForOccurrence(a: RecurringTask, b: RecurringTask, month: number, year: number): number {
  const dueA = getOccurrenceDueDate(year, month, a.deadlineType ?? DEFAULT_TASK_DEADLINE).getTime();
  const dueB = getOccurrenceDueDate(year, month, b.deadlineType ?? DEFAULT_TASK_DEADLINE).getTime();

  if (dueA !== dueB) {
    return dueA - dueB;
  }

  return a.title.localeCompare(b.title, 'sv');
}

function compareTaskOccurrences(a: TaskOccurrence, b: TaskOccurrence): number {
  const dueA = getOccurrenceDueDate(a.year, a.month, a.task.deadlineType ?? DEFAULT_TASK_DEADLINE).getTime();
  const dueB = getOccurrenceDueDate(b.year, b.month, b.task.deadlineType ?? DEFAULT_TASK_DEADLINE).getTime();

  if (dueA !== dueB) {
    return dueA - dueB;
  }

  return a.task.title.localeCompare(b.task.title, 'sv');
}

// Standarduppgifter som förslag
interface TaskSuggestion {
  title: string;
  description: string;
  area: PrepArea;
  month: number;
  frequency: TaskFrequency;
}

const TASK_SUGGESTIONS: TaskSuggestion[] = [
  // Januari
  { title: 'Testa brandvarnare', description: 'Kontrollera att alla brandvarnare fungerar.', area: 'safety', month: 0, frequency: 'quarterly' },
  { title: 'Kontrollera brandsläckare', description: 'SKontrollera att brandsläckarens tryck är okej', area: 'safety', month: 0, frequency: 'quarterly' },
  { title: 'Kontrollera brandfilt', description: 'Kontrollera att brandfilten är lättåtkomlig och i gott skick.', area: 'safety', month: 0, frequency: 'quarterly' },
  { title: 'Rotera vattenförrådet', description: 'Töm och fyll på dunkar med färskt vatten.', area: 'water', month: 0, frequency: 'biannual' },
  { title: 'Kontrollera första hjälpen-väska', description: 'Fyll på det som saknas eller har gått ut.', area: 'health', month: 0, frequency: 'biannual' },
  { title: 'Kontrollera husapotek', description: 'Kontrollera mediciner och fyll på vid behov.', area: 'health', month: 0, frequency: 'biannual' },
  { title: 'Kontrollera evakueringsväska', description: 'Gå igenom innehållet och uppdatera efter säsong.', area: 'transport', month: 0, frequency: 'biannual' },
  { title: 'Testa nödradio (Sveriges Radio P4)', description: 'Kontrollera att radion fungerar och hitta P4.', area: 'information', month: 0, frequency: 'biannual' },
  { title: 'Kontrollera och uppdatera kontaktlista', description: 'Se till att nödkontakter är aktuella.', area: 'communication', month: 0, frequency: 'annual' },
  // April
  { title: 'Öva evakueringsplan', description: 'Gå igenom evakueringsplanen med familjen.', area: 'transport', month: 3, frequency: 'annual' },
  { title: 'Kontrollera vattenbehållare', description: 'Inspektera behållare för sprickor och läckor.', area: 'water', month: 3, frequency: 'annual' },
  // Maj
  { title: 'Testa stormkök/gasolkök', description: 'Prova att köket fungerar och att det finns tillräckligt med bränsle.', area: 'food', month: 4, frequency: 'biannual' },
  // Juni
  { title: 'Inventera beredskapsmaten', description: 'Gå igenom matförrådet, kontrollera datum och planera rotation.', area: 'food', month: 5, frequency: 'biannual' },
  // Augusti
  { title: 'Kontrollera bilens beredskapsutrustning', description: 'Ficklampa, filtar, varningstriangel, startkablar och förbandslåda.', area: 'transport', month: 7, frequency: 'biannual' },
  { title: 'Inspektera sovsäckar och filtar', description: 'Kontrollera skick och tvätta vid behov.', area: 'shelter', month: 7, frequency: 'annual' },
  // November
  { title: 'Repetera första hjälpen', description: 'Friska upp kunskaperna i HLR och sårvård.', area: 'health', month: 10, frequency: 'annual' },
];

type ViewMode = 'wheel' | 'list' | 'overdue';

// Kategori-anpassade avdelningar
const CATEGORY_AISLES: Record<string, string[]> = {
  mat: ['Frukt & Grönt', 'Mejeri', 'Kött & Chark', 'Fisk & Skaldjur', 'Bröd & Bageri', 'Konserver', 'Torrvaror', 'Fryst', 'Drycker', 'Godis & Snacks'],
  medicin: ['Apotek', 'Hygien', 'Hälsokost'],
  hygien: ['Hygien', 'Kemi & Städning', 'Baby'],
  verktyg: ['Hushåll', 'Järnhandel', 'Byggvaror'],
  kommunikation: ['Elektronik', 'Hushåll'],
  varme: ['Hushåll', 'Järnhandel', 'Byggvaror'],
  ljus: ['Hushåll', 'Järnhandel'],
  dokument: ['Kontorsmaterial', 'Hushåll'],
  ovrigt: ['Frukt & Grönt', 'Mejeri', 'Kött & Chark', 'Fisk & Skaldjur', 'Bröd & Bageri', 'Konserver', 'Torrvaror', 'Fryst', 'Drycker', 'Godis & Snacks', 'Kemi & Städning', 'Hygien', 'Djurmat', 'Hushåll', 'Baby'],
};
const COMMON_AISLES_YW = ['Frukt & Grönt', 'Mejeri', 'Kött & Chark', 'Fisk & Skaldjur', 'Bröd & Bageri', 'Konserver', 'Torrvaror', 'Fryst', 'Drycker', 'Godis & Snacks', 'Kemi & Städning', 'Hygien', 'Djurmat', 'Hushåll', 'Baby'];

// Custom dropdown component för områdesfilter
interface AreaFilterDropdownProps {
  value: PrepArea | 'all';
  onChange: (value: PrepArea | 'all') => void;
  areaConfig: Record<PrepArea, { icon: LucideIcon; color: string; name: string }>;
  isActive?: boolean;
}

function AreaFilterDropdown({ value, onChange, areaConfig, isActive }: AreaFilterDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedLabel = value === 'all' 
    ? t('yearWheel.allAreas', 'Alla områden')
    : areaConfig[value as PrepArea]?.name || '';

  const SelectedIcon = value !== 'all' ? areaConfig[value as PrepArea]?.icon : null;

  return (
    <div ref={dropdownRef} className="relative flex-1 sm:flex-none">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full sm:min-w-[200px] px-3 py-2 rounded-lg border text-foreground text-sm flex items-center justify-between gap-2 hover:bg-muted transition-colors ${
          isActive
            ? 'border-slate-300 bg-input shadow-[0_0_8px_rgba(148,163,184,0.6)]'
            : 'border-border bg-input'
        }`}
      >
        <span className="flex items-center gap-2">
          {SelectedIcon && <SelectedIcon className="h-4 w-4" style={{ color: areaConfig[value as PrepArea]?.color }} />}
          {selectedLabel}
        </span>
        <div className="flex items-center gap-1">
          {value !== 'all' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('all');
                setIsOpen(false);
              }}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              title={t('yearWheel.allAreas', 'Alla områden')}
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[300px] overflow-y-auto">
          <button
            type="button"
            onClick={() => {
              onChange('all');
              setIsOpen(false);
            }}
            className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2 ${
              value === 'all' ? 'bg-muted font-medium' : ''
            }`}
          >
            {t('yearWheel.allAreas', 'Alla områden')}
          </button>
          
          {Object.entries(areaConfig).map(([key, { icon: Icon, color, name }]) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                onChange(key as PrepArea);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2 ${
                value === key ? 'bg-muted font-medium' : ''
              }`}
            >
              <Icon className="h-4 w-4" style={{ color }} />
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Tag Filter Dropdown
interface TagFilterDropdownProps {
  value: string;
  onChange: (value: string) => void;
  allTags: string[];
  isActive?: boolean;
}

function TagFilterDropdown({ value, onChange, allTags, isActive }: TagFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedLabel = value === 'all' 
    ? 'Alla taggar'
    : value;

  return (
    <div ref={dropdownRef} className="relative flex-1 sm:flex-none">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full sm:min-w-[180px] px-3 py-2 rounded-lg border text-foreground text-sm flex items-center justify-between gap-2 hover:bg-muted transition-colors ${
          isActive
            ? 'border-slate-300 bg-input shadow-[0_0_8px_rgba(148,163,184,0.6)]'
            : 'border-border bg-input'
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          {selectedLabel}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value !== 'all' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('all');
                setIsOpen(false);
              }}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              title="Alla taggar"
              aria-label="Rensa taggfilter (visa alla taggar)"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[300px] overflow-y-auto">
          <button
            type="button"
            onClick={() => {
              onChange('all');
              setIsOpen(false);
            }}
            className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors ${
              value === 'all' ? 'bg-muted font-medium' : ''
            }`}
          >
            Alla taggar
          </button>
          
          {allTags.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground italic">
              Inga taggar ännu
            </div>
          ) : (
            allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => {
                  onChange(tag);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors ${
                  value === tag ? 'bg-muted font-medium' : ''
                }`}
              >
                {tag}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Custom dropdown för område (utan "Alla områden")
interface AreaDropdownProps {
  value: PrepArea;
  onChange: (value: PrepArea) => void;
  label?: string;
  required?: boolean;
}

function AreaDropdown({ value, onChange, label, required }: AreaDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedArea = areaConfig[value];
  const SelectedIcon = selectedArea.icon;

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1">
          {label} {required && '*'}
        </label>
      )}
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-2 border border-border bg-input text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary flex items-center justify-between gap-2 hover:bg-muted transition-colors"
        >
          <span className="flex items-center gap-2">
            <SelectedIcon className="h-4 w-4" style={{ color: selectedArea.color }} />
            {selectedArea.name}
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[300px] overflow-y-auto">
            {Object.entries(areaConfig).map(([key, { icon: Icon, color, name }]) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  onChange(key as PrepArea);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2 ${
                  value === key ? 'bg-muted font-medium' : ''
                }`}
              >
                <Icon className="h-4 w-4" style={{ color }} />
                {name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Custom dropdown för frekvens
interface FrequencyDropdownProps {
  value: TaskFrequency;
  onChange: (value: TaskFrequency) => void;
  label?: string;
}

function FrequencyDropdown({ value, onChange, label }: FrequencyDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1">
          {label}
        </label>
      )}
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-2 border border-border bg-input text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary flex items-center justify-between gap-2 hover:bg-muted transition-colors"
        >
          <span>{frequencyLabels[value]}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50">
            {Object.entries(frequencyLabels).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  onChange(key as TaskFrequency);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors ${
                  value === key ? 'bg-muted font-medium' : ''
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Custom dropdown för månad
interface MonthDropdownProps {
  value: number;
  onChange: (value: number) => void;
  label?: string;
}

function MonthDropdown({ value, onChange, label }: MonthDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1">
          {label}
        </label>
      )}
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-2 border border-border bg-input text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary flex items-center justify-between gap-2 hover:bg-muted transition-colors"
        >
          <span>{months[value]}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[300px] overflow-y-auto">
            {months.map((monthName, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  onChange(idx);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors ${
                  value === idx ? 'bg-muted font-medium' : ''
                }`}
              >
                {monthName}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface DeadlineDropdownProps {
  value: TaskDeadline;
  onChange: (value: TaskDeadline) => void;
  label?: string;
}

function DeadlineDropdown({ value, onChange, label }: DeadlineDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1">
          {label}
        </label>
      )}
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-2 border border-border bg-input text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary flex items-center justify-between gap-2 hover:bg-muted transition-colors"
        >
          <span>{deadlineLabels[value]}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50">
            {(Object.keys(deadlineLabels) as TaskDeadline[]).map((deadlineKey) => (
              <button
                key={deadlineKey}
                type="button"
                onClick={() => {
                  onChange(deadlineKey);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors ${
                  value === deadlineKey ? 'bg-muted font-medium' : ''
                }`}
              >
                {deadlineLabels[deadlineKey]}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const YearWheelPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { 
    tasks, 
    completeTask, 
    addTask, 
    deleteTask,
    updateTask,
    getOverdueTasks,
    getTasksByMonth,
    fetchFromServer,
    createDefaultTasks 
  } = useYearWheelStore();
  const { addPoints } = useGamificationStore();
  const { user, isAuthenticated } = useAuthStore();
  const family = useFamilyStore((s) => s.family);
  const isFamilyOwner = useFamilyStore((s) => s.isOwner);
  const hydrated = useYearWheelHydrated();
  const scrollPositionRef = useRef<number>(0);
  const isTransitioningModalsRef = useRef<boolean>(false);
  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const monthHeadingRef = useRef<HTMLDivElement | null>(null);
  
  const [viewMode, setViewMode] = useState<ViewMode>('wheel');
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 5;
  const maxYear = currentYear + 5;
  const [showAddModal, setShowAddModal] = useState(false);
  const [copyTaskData, setCopyTaskData] = useState<RecurringTask | null>(null);
  const [showTaskModal, setShowTaskModal] = useState<RecurringTask | null>(null);
  const [showEditModal, setShowEditModal] = useState<RecurringTask | null>(null);
  const [filterArea, setFilterArea] = useState<PrepArea | 'all'>('all');
  const [filterTag, setFilterTag] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [hideCompleted, setHideCompleted] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [showCSVExportModal, setShowCSVExportModal] = useState(false);
  const [exportAreas, setExportAreas] = useState<PrepArea[]>([]);

  // Shopping list modal state
  const [showShoppingModal, setShowShoppingModal] = useState(false);
  const [selectedTaskForShopping, setSelectedTaskForShopping] = useState<RecurringTask | null>(null);
  const [shoppingQuantity, setShoppingQuantity] = useState(1);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [shoppingCategory, setShoppingCategory] = useState<InventoryCategory>('verktyg');
  const [shoppingNotes, setShoppingNotes] = useState('');
  const [shoppingStore, setShoppingStore] = useState('');
  const [shoppingAisle, setShoppingAisle] = useState('');
  const [shoppingPrice, setShoppingPrice] = useState('0');
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [showStoreSuggestions, setShowStoreSuggestions] = useState(false);
  const [storeSuggestionSearch, setStoreSuggestionSearch] = useState('');
  const [showAisleSuggestions, setShowAisleSuggestions] = useState(false);
  const [aisleSuggestionSearch, setAisleSuggestionSearch] = useState('');

  const { lists: shoppingLists, createItem: createShoppingItem, createList: createShoppingList } = useShoppingListStore();
  const shoppingItems = useShoppingListStore(state => state.items);

  const openShoppingModal = (task: RecurringTask) => {
    setSelectedTaskForShopping(task);
    setShoppingQuantity(1);
    setShoppingCategory('verktyg');
    setShoppingNotes('');
    setShoppingStore('');
    setShoppingAisle('');
    setShoppingPrice('0');
    setShowNewListForm(false);
    setNewListName('');
    setSelectedListId(shoppingLists.find(l => l.isPrimary)?.id || shoppingLists[0]?.id || null);
    setShowShoppingModal(true);
  };

  const closeShoppingModal = () => {
    setShowShoppingModal(false);
    setSelectedTaskForShopping(null);
    setSelectedListId(null);
    setShoppingQuantity(1);
    setShoppingNotes('');
    setShowNewListForm(false);
    setNewListName('');
    setShoppingStore('');
    setShoppingAisle('');
    setShoppingPrice('0');
  };

  useEffect(() => {
    if (showShoppingModal) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showShoppingModal]);

  const [exportTags, setExportTags] = useState<string[]>([]);
  const [hideFab, setHideFab] = useState(false);
  const [gridColumns, setGridColumns] = useState<'single' | 'gallery'>(() => {
    try {
      const stored = localStorage.getItem('yearWheelGridColumns');
      return (stored === 'single' || stored === 'gallery') ? stored : 'gallery';
    } catch {
      return 'gallery';
    }
  });
  
  // Save grid columns preference to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('yearWheelGridColumns', gridColumns);
    } catch {
      // ignore
    }
  }, [gridColumns]);

  const canNavigateToPreviousMonth = selectedYear > minYear || selectedMonth > 0;
  const canNavigateToNextMonth = selectedYear < maxYear || selectedMonth < 11;

  const resetMonthRelatedFilters = useCallback(() => {
    setSearchQuery('');
    setFilterTag('all');
  }, []);

  const goToPreviousMonth = useCallback(() => {
    const raw = selectedMonth - 1;

    if (raw < 0) {
      if (selectedYear > minYear) {
        setSelectedMonth(11);
        setSelectedYear((year) => year - 1);
      }
    } else {
      setSelectedMonth(raw);
    }

    resetMonthRelatedFilters();
  }, [minYear, resetMonthRelatedFilters, selectedMonth, selectedYear]);

  const goToNextMonth = useCallback(() => {
    const raw = selectedMonth + 1;

    if (raw > 11) {
      if (selectedYear < maxYear) {
        setSelectedMonth(0);
        setSelectedYear((year) => year + 1);
      }
    } else {
      setSelectedMonth(raw);
    }

    resetMonthRelatedFilters();
  }, [maxYear, resetMonthRelatedFilters, selectedMonth, selectedYear]);

  const goToCurrentMonth = useCallback(() => {
    const now = new Date();
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth());
    resetMonthRelatedFilters();
  }, [resetMonthRelatedFilters]);

  const openAddTaskModal = useCallback(() => {
    const hasPremiumAccess = user?.subscriptionTier === 'premium' || !!family;
    if (hasPremiumAccess) {
      if (tasks.length >= 2000) {
        return;
      }

      setShowAddModal(true);
      return;
    }

    setShowPremiumModal(true);
  }, [family, tasks.length, user?.subscriptionTier]);

  const scrollMonthHeadingIntoView = useCallback(() => {
    if (!monthHeadingRef.current) return;

    try {
      const rect = monthHeadingRef.current.getBoundingClientRect();
      const currentScrollY = typeof window !== 'undefined' ? window.scrollY : 0;
      const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
      const offset = isDesktop ? 24 : 55;
      const targetY = currentScrollY + rect.top - offset;

      if (typeof window !== 'undefined') {
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
    } catch {
      monthHeadingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const goToPreviousMonthFromFab = useCallback(() => {
    goToPreviousMonth();
    setTimeout(() => {
      scrollMonthHeadingIntoView();
    }, 50);
  }, [goToPreviousMonth, scrollMonthHeadingIntoView]);

  const goToNextMonthFromFab = useCallback(() => {
    goToNextMonth();
    setTimeout(() => {
      scrollMonthHeadingIntoView();
    }, 50);
  }, [goToNextMonth, scrollMonthHeadingIntoView]);

  const transitionFromEditModalToTaskModal = useCallback((task: RecurringTask) => {
    isTransitioningModalsRef.current = true;
    setShowTaskModal(task);
    setShowEditModal(null);

    setTimeout(() => {
      isTransitioningModalsRef.current = false;
    }, 50);
  }, []);
  
  const hasGardeningTasks = useMemo(() => {
    return tasks.some((task) => task.area === 'gardening');
  }, [tasks]);
  
  // Export tasks to CSV
  const handleExportCSV = (tasksToExport: RecurringTask[] = tasks) => {
    // Reverse mappings for export
    const areaToSwedish: Record<PrepArea, string> = {
      water: 'Vatten',
      food: 'Mat',
      energy: 'Energi',
      health: 'Hälsa',
      information: 'Information',
      shelter: 'Skydd',
      transport: 'Transport',
      communication: 'Kommunikation',
      safety: 'Säkerhet',
      shopping: 'Inköp',
      gardening: 'Odla',
      other: 'Övrigt',
    };

    const frequencyToSwedish: Record<TaskFrequency, string> = {
      monthly: 'Månad',
      quarterly: 'Kvartal',
      biannual: 'Halvår',
      annual: 'År',
    };

    const monthNames = [
      'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
      'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'
    ];

    // Create CSV header (inklusive taggar)
    let csvContent = 'Titel;Beskrivning;Område;Frekvens;Startmånad;Förfallodatum;Taggar\n';

    // Add each task
    tasksToExport.forEach(task => {
      // Escape description if it contains semicolons or newlines
      const needsQuotes = task.description.includes(';') || task.description.includes('\n');
      const description = needsQuotes ? `"${task.description}"` : task.description;

      const tags = task.tags && task.tags.length > 0
        ? task.tags.join(',')
        : '';
      
      const row = [
        task.title,
        description,
        areaToSwedish[task.area],
        frequencyToSwedish[task.frequency],
        monthNames[task.month],
        deadlineLabels[task.deadlineType || DEFAULT_TASK_DEADLINE],
        tags,
      ].join(';');
      
      csvContent += row + '\n';
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().split('T')[0];
    
    link.setAttribute('href', url);
    link.setAttribute('download', `arshjul-export-${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Check if any default tasks are missing (based on title)
  const missingDefaultTasks = useMemo(() => {
    const existingTitles = new Set(tasks.map(t => t.title.toLowerCase().trim()));
    return TASK_SUGGESTIONS.filter(suggestion => 
      !existingTitles.has(suggestion.title.toLowerCase().trim())
    );
  }, [tasks]);
  
  const hasMissingDefaultTasks = missingDefaultTasks.length > 0;

  const scrollFiltersIntoView = useCallback(() => {
    if (!filterContainerRef.current) return;

    try {
      const rect = filterContainerRef.current.getBoundingClientRect();
      const currentScrollY = typeof window !== 'undefined' ? window.scrollY : 0;

      // Scroll so that filterboxarna hamnar lite längre ner i vyn
      const offset = 80; // px ovanför filterraden
      const targetY = currentScrollY + rect.top - offset;

      if (typeof window !== 'undefined') {
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
    } catch {
      // Fallback om något går snett
      filterContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
  
  // Dölj flytande Lägg till-knapp när mobilmenyn är öppen (Layout dispatchar 'mobile-menu')
  useEffect(() => {
    // initial state from sessionStorage
    try {
      const stored = sessionStorage.getItem('mobileMenuOpen');
      if (stored === 'true') setHideFab(true);
    } catch {
      // ignore
    }

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<boolean>)?.detail;
      setHideFab(Boolean(detail));
    };

    window.addEventListener('mobile-menu', handler as EventListener);
    return () => window.removeEventListener('mobile-menu', handler as EventListener);
  }, []);

  // Scroll-locking för modaler
  useLayoutEffect(() => {
    const isModalOpen = showAddModal || !!showTaskModal || !!showEditModal || showCSVImportModal || showCSVExportModal;
    if (isModalOpen) {
      // Save current scroll position and lock scroll (only if not already saved)
      if (!document.documentElement.classList.contains('modal-open')) {
        const currentScroll = window.scrollY;
        scrollPositionRef.current = currentScroll;
        document.documentElement.classList.add('modal-open');
        document.body.style.position = 'fixed';
        document.body.style.top = `-${currentScroll}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
      }
    } else {
      // Restore scroll position only if we have a saved position and not transitioning between modals
      if (document.documentElement.classList.contains('modal-open') && !isTransitioningModalsRef.current) {
        const scrollPos = scrollPositionRef.current;
        document.documentElement.classList.remove('modal-open');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        window.scrollTo(0, scrollPos);
        scrollPositionRef.current = 0;
      }
    }
  }, [showAddModal, showTaskModal, showEditModal, showCSVImportModal, showCSVExportModal]);
  
  // Ladda uppgifter när komponenten mountas
  useEffect(() => {
    if (hydrated && isAuthenticated) {
      // Resolve canonical family id immediately to avoid hydration flicker
      (async () => {
        try {
          const { getEffectiveFamilyOwnerId } = await import('../stores/family-store');
          // Resolve canonical family id to warm up family-store and avoid flicker
          getEffectiveFamilyOwnerId();
          fetchFromServer();
        } catch {
          fetchFromServer();
        }
      })();
    }
  }, [hydrated, isAuthenticated, fetchFromServer]);
  
  // Scroll to task if navigated from Dashboard
  useEffect(() => {
    const state = location.state as { scrollToTaskId?: string; viewMode?: ViewMode } | null;
    if (state?.scrollToTaskId && tasks.length > 0) {
      // Wait for DOM to update
      setTimeout(() => {
        const element = document.querySelector(`[data-task-id="${state.scrollToTaskId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Clear the state to prevent scrolling again on re-render
          window.history.replaceState({}, document.title);
        }
      }, 100);
    }
    // Set viewMode if passed from Dashboard (e.g., clicking overdue warning)
    if (state?.viewMode) {
      setViewMode(state.viewMode);
      // Clear the state to prevent re-setting on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state, tasks]);

  // Ensure derived lists recompute when underlying `tasks` change.
  // Include `tasks` directly so the memo invalidates when any task updates
  // (e.g. after marking complete with optimistic update).
   
  const overdueTasks = useMemo(() => getOverdueTasks(), [tasks, getOverdueTasks]);

  // Compute tasks for the selected month. Include `tasks` so the memo
  // invalidates when task state changes (e.g. lastCompleted updates).
   
  const monthTasks = useMemo(() => {
    const tasksInMonth = getTasksByMonth(selectedMonth, selectedYear);
    const areaFiltered = filterArea === 'all' ? tasksInMonth : tasksInMonth.filter(task => task.area === filterArea);
    const visibleTasks = hideCompleted
      ? areaFiltered.filter(task => !isTaskCompletedForPeriod(task, selectedMonth, selectedYear))
      : areaFiltered;

    return [...visibleTasks].sort((a, b) => compareTasksForOccurrence(a, b, selectedMonth, selectedYear));
   
  }, [tasks, selectedMonth, selectedYear, filterArea, hideCompleted, getTasksByMonth]);

  // Randomised tile data for the Odlingsläget background.
  // Each tile shows a different vertical crop of the image so the repeat looks organic.
  const gardenBgTiles = useMemo(() => {
    const tiles: { posY: number; topVh: number; heightVh: number }[] = [];
    let top = 0;
    for (let i = 0; i < 10; i++) {
      const heightVh = 38 + Math.random() * 42; // 38–80 vh per tile
      tiles.push({ posY: Math.floor(Math.random() * 100), topVh: top, heightVh });
      top += heightVh;
    }
    return tiles;
  }, []);

  // Get all unique tags from all tasks
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    tasks.forEach(task => {
      if (task.tags && task.tags.length > 0) {
        task.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b, 'sv'));
  }, [tasks]);

  // Get all unique tags for gardening tasks (used for desktop quick filters in Odlingsläget)
  const gardeningTags = useMemo(() => {
    const tagSet = new Set<string>();
    tasks.forEach(task => {
      if (task.area === 'gardening' && task.tags && task.tags.length > 0) {
        task.tags.forEach(tag => {
          const trimmed = tag.trim();
          if (trimmed) {
            tagSet.add(trimmed);
          }
        });
      }
    });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b, 'sv'));
  }, [tasks]);

  // Tag filter results (similar to search results)
  const tagFilterResults = useMemo(() => {
    if (filterTag === 'all') return [];
    
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const results: TaskOccurrence[] = [];
    
    // Filter tasks by tag
    const matchingTasks = tasks.filter(task => {
      const matchesTag = !!(task.tags && task.tags.includes(filterTag));
      const matchesArea = filterArea === 'all' || task.area === filterArea;
      return matchesTag && matchesArea;
    });
    
    // Generate occurrences for next 12 months
    for (let i = 0; i < 12; i++) {
      const targetMonth = (currentMonth + i) % 12;
      const targetYear = currentYear + Math.floor((currentMonth + i) / 12);
      
      matchingTasks.forEach(task => {
        const tasksInMonth = getTasksByMonth(targetMonth, targetYear);
        const occursThisMonth = tasksInMonth.some(t => t.id === task.id);
        
        if (occursThisMonth) {
          if (hideCompleted && isTaskCompletedForPeriod(task, targetMonth, targetYear)) return;
          results.push({
            task,
            month: targetMonth,
            year: targetYear,
            nextDue: task.nextDue,
          });
        }
      });
    }
    
    return results.sort(compareTaskOccurrences);
  }, [filterTag, filterArea, hideCompleted, tasks, getTasksByMonth]);

  // Group tag filter results by month
  const groupedTagFilterResults = useMemo(() => {
    const grouped: Record<string, typeof tagFilterResults> = {};
    
    tagFilterResults.forEach(result => {
      const key = `${result.year}-${result.month}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(result);
    });

    Object.values(grouped).forEach(results => {
      results.sort(compareTaskOccurrences);
    });

    return grouped;
  }, [tagFilterResults]);

  // Generate search results with all occurrences for next 12 months
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase();
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const results: TaskOccurrence[] = [];
    
    // Filter tasks by search query
    const matchingTasks = tasks.filter(task => {
      const matchesSearch =
        task.title.toLowerCase().includes(query) ||
        task.description.toLowerCase().includes(query);
      const matchesArea = filterArea === 'all' || task.area === filterArea;
      return matchesSearch && matchesArea;
    });
    
    // Generate occurrences for next 12 months
    for (let i = 0; i < 12; i++) {
      const targetMonth = (currentMonth + i) % 12;
      const targetYear = currentYear + Math.floor((currentMonth + i) / 12);
      
      matchingTasks.forEach(task => {
        const tasksInMonth = getTasksByMonth(targetMonth, targetYear);
        const occursThisMonth = tasksInMonth.some(t => t.id === task.id);
        
        if (occursThisMonth) {
          if (hideCompleted && isTaskCompletedForPeriod(task, targetMonth, targetYear)) return;
          results.push({
            task,
            month: targetMonth,
            year: targetYear,
            nextDue: task.nextDue, // Use the actual nextDue from task
          });
        }
      });
    }
    
    return results.sort(compareTaskOccurrences);
  }, [searchQuery, filterArea, hideCompleted, tasks, getTasksByMonth]);

  // Group search results by month
  const groupedSearchResults = useMemo(() => {
    const grouped: Record<string, typeof searchResults> = {};
    
    searchResults.forEach(result => {
      const key = `${result.year}-${result.month}`;

    Object.values(grouped).forEach(results => {
      results.sort(compareTaskOccurrences);
    });

      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(result);
    });
    
    return grouped;
  }, [searchResults]);

  const handleCompleteTask = async (task: RecurringTask) => {
    const shouldExitOverdueView =
      viewMode === 'overdue' &&
      overdueTasks.length === 1 &&
      overdueTasks[0]?.id === task.id;

    // Stäng modalen DIREKT för bättre UX - optimistic update i store kommer visa ändringen
    setShowTaskModal(null);
    
    // Ge poäng för att slutföra en underhållsuppgift
    addPoints('resource', task.area, POINT_VALUES.COMPLETE_TASK, `Slutförde: ${task.title}`);

    // Markera som slutförd (optimistisk uppdatering + server sync händer i bakgrunden)
    try {
      await completeTask(task.id);

      if (shouldExitOverdueView) {
        setViewMode('wheel');
      }
    } catch (error) {
      console.error('Kunde inte slutföra uppgift:', error);
    }
  };

  const handleRewindTask = async (task: RecurringTask) => {
    const intervalMonths = getFrequencyIntervalMonths(task.frequency);

    const currentNextDue = new Date(task.nextDue);
    const previousOccurrenceMonth = new Date(
      currentNextDue.getFullYear(),
      currentNextDue.getMonth() - intervalMonths,
      1,
    );
    const prevDate = getOccurrenceDueDate(
      previousOccurrenceMonth.getFullYear(),
      previousOccurrenceMonth.getMonth(),
      task.deadlineType || DEFAULT_TASK_DEADLINE,
    );
    const prevNextDue = formatDateToYmd(prevDate);

    try {
      await updateTask(task.id, { nextDue: prevNextDue });
      // Uppdatera modal-uppgiften så det nya datumet visas direkt
      setShowTaskModal(prev => prev ? { ...prev, nextDue: prevNextDue } : null);
    } catch (error) {
      console.error('Kunde inte ångra datum:', error);
    }
  };

  const getDaysUntil = (dateStr: string): number => {
    const due = new Date(dateStr);
    const now = new Date();
    return Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getStatusColor = (dateStr: string): string => {
    const days = getDaysUntil(dateStr);
    if (days < 0) return 'text-red-400 bg-red-900/30';
    if (days <= 7) return 'text-orange-400 bg-orange-900/30';
    if (days <= 30) return 'text-yellow-400 bg-yellow-900/30';
    return 'text-primary bg-primary/20';
  };

  // True when a tag or text filter is active — month glow is suppressed in this state
  const isFilterActive = searchQuery.trim() !== '' || filterTag !== 'all';

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground flex items-center"><CalendarDays className="h-6 w-6 text-primary mr-3 shrink-0" />{t('yearWheel.title', 'Årshjul')}</h1>
        <p className="text-muted-foreground mt-1">Planera återkommande beredskapsunderhåll och viktiga säkerhetsåtgärder under året.</p>
      </div>

      {/* View Tabs & Filters */}
      <div className="flex flex-col gap-4 mb-6">
        {/* View mode tabs - (Månadsvy removed) */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          {overdueTasks.length > 0 && (
            <button
              onClick={() => {
                const nextMode = viewMode === 'overdue' ? 'wheel' : 'overdue';
                setViewMode(nextMode);

                if (nextMode === 'overdue') {
                  // Nollställ övriga filter när man går in i Försenade-vyn
                  setFilterArea('all');
                  setFilterTag('all');
                  setSearchQuery('');
                }
              }}
              className={`flex-shrink-0 px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm ${
                viewMode === 'overdue' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
              }`}
            >
              <AlertTriangle className="h-4 w-4" /> {t('yearWheel.overdueView', 'Försenade uppgifter')} ({overdueTasks.length})
            </button>
          )}
        </div>
        
        {/* Button row: Create tasks from template (shown when default tasks are missing) */}
        {hasMissingDefaultTasks && (
          <div className="mb-2">
            <button
              onClick={async () => {
                // Check if user has Premium or is in a family with Premium owner
                const hasPremiumAccess = user?.subscriptionTier === 'premium' || !!family;
                if (hasPremiumAccess) {
                  await createDefaultTasks();
                } else {
                  setShowPremiumModal(true);
                }
              }}
              className="px-3 py-2 bg-secondary text-secondary-foreground rounded-lg font-medium hover:bg-secondary/90 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Plus className="h-4 w-4" /> Lägg till standarduppgifter ({missingDefaultTasks.length})
            </button>
          </div>
        )}

        {/* Filters - keep add button on same row; allow horizontal scroll on small screens */}
        <div className="flex flex-col gap-2">
          {/* Year, Month navigation and Import button */}
          <div className="flex items-center gap-2">
            {viewMode !== 'overdue' && (
              <div className="flex items-center gap-2 overflow-x-auto flex-1">
              <button
                onClick={() => { setSelectedYear((y) => Math.max(minYear, y - 1)); setSearchQuery(''); setFilterTag('all'); }}
                disabled={selectedYear <= minYear}
                className="px-2 py-2 rounded-lg border border-border bg-input hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Föregående år"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div
                onClick={goToCurrentMonth}
                className="px-2 py-2 rounded-lg border border-border bg-input text-sm font-medium min-w-[70px] text-center cursor-pointer select-none"
                title="Klicka för att gå till aktuellt år och månad"
              >
                {selectedYear}
              </div>
              <button
                onClick={() => { setSelectedYear((y) => Math.min(maxYear, y + 1)); setSearchQuery(''); setFilterTag('all'); }}
                disabled={selectedYear >= maxYear}
                className="px-2 py-2 rounded-lg border border-border bg-input hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Nästa år"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              
              <button
                onClick={goToPreviousMonth}
                disabled={!canNavigateToPreviousMonth}
                className="px-2 py-2 rounded-lg border border-border bg-input hover:bg-muted transition-colors"
                title="Föregående månad"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div
                role="button"
                tabIndex={0}
                onClick={goToCurrentMonth}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    goToCurrentMonth();
                    e.preventDefault();
                  }
                }}
                className="px-2 py-2 rounded-lg border border-border bg-input text-sm font-medium min-w-[48px] text-center select-none text-foreground cursor-pointer"
                aria-label="Gå till aktuell månad"
              >
                {months[selectedMonth].substring(0,3)}
              </div>
              <button
                onClick={goToNextMonth}
                disabled={!canNavigateToNextMonth}
                className="px-2 py-2 rounded-lg border border-border bg-input hover:bg-muted transition-colors"
                title="Nästa månad"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              </div>
            )}
            
            {/* CSV Export & Import buttons - only visible on desktop for Premium users who are not family members, or are family owners */}
            {viewMode !== 'overdue' && user?.subscriptionTier === 'premium' && (!family || isFamilyOwner) && (
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={() => {
                    const allAreaKeys = Object.keys(areaConfig) as PrepArea[];
                    setExportAreas(allAreaKeys);
                    setExportTags(allTags);
                    setShowCSVExportModal(true);
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-input hover:bg-muted transition-colors shrink-0"
                  title="Exportera till CSV"
                >
                  <FileDown className="h-4 w-4" />
                  <span className="text-sm">Exportera</span>
                </button>
                <button
                  onClick={() => setShowCSVImportModal(true)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-input hover:bg-muted transition-colors shrink-0"
                  title="Importera från CSV"
                >
                  <FileUp className="h-4 w-4" />
                  <span className="text-sm">Importera</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Month Selector (Wheel View) */}
      {viewMode === 'wheel' && (
        <div className="mb-6">
          {/* Mobile: Grid layout */}
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-2">
            {months.map((month, idx) => {
              const tasksInMonth = getTasksByMonth(idx, selectedYear);
              
              // Färgschema baserat på årstider
              // Vinter (dec, jan, feb): Blå → Ljusblå
              // Vår (mar, apr, maj): Orange → Ljusorange → Mörkgul
              // Sommar (jun, jul, aug): Gul → Klargul → Mörkgul
              // Höst (sep, okt, nov): Ljusorange → Orange → Ljusblå
              const seasonColors = {
                0: { light: 'bg-blue-50', dark: 'dark:bg-blue-950/30', text: 'text-blue-700', darkText: 'dark:text-blue-300', border: 'border-blue-500', darkBorder: 'dark:border-blue-400', shadow: 'shadow-[0_0_12px_rgba(59,130,246,0.5)]', darkShadow: 'dark:shadow-[0_0_12px_rgba(96,165,250,0.4)]', badgeBg: 'bg-blue-100', badgeDarkBg: 'dark:bg-blue-900/50', badgeText: 'text-blue-700', badgeDarkText: 'dark:text-blue-300', badgeBorder: 'border-blue-300', badgeDarkBorder: 'dark:border-blue-600' },
                1: { light: 'bg-sky-50', dark: 'dark:bg-sky-950/30', text: 'text-sky-700', darkText: 'dark:text-sky-300', border: 'border-sky-500', darkBorder: 'dark:border-sky-400', shadow: 'shadow-[0_0_12px_rgba(14,165,233,0.5)]', darkShadow: 'dark:shadow-[0_0_12px_rgba(56,189,248,0.4)]', badgeBg: 'bg-sky-100', badgeDarkBg: 'dark:bg-sky-900/50', badgeText: 'text-sky-700', badgeDarkText: 'dark:text-sky-300', badgeBorder: 'border-sky-300', badgeDarkBorder: 'dark:border-sky-600' },
                2: { light: 'bg-orange-50', dark: 'dark:bg-orange-950/30', text: 'text-orange-700', darkText: 'dark:text-orange-300', border: 'border-orange-500', darkBorder: 'dark:border-orange-400', shadow: 'shadow-[0_0_12px_rgba(249,115,22,0.5)]', darkShadow: 'dark:shadow-[0_0_12px_rgba(251,146,60,0.4)]', badgeBg: 'bg-orange-100', badgeDarkBg: 'dark:bg-orange-900/50', badgeText: 'text-orange-700', badgeDarkText: 'dark:text-orange-300', badgeBorder: 'border-orange-300', badgeDarkBorder: 'dark:border-orange-600' },
                3: { light: 'bg-orange-50', dark: 'dark:bg-orange-950/30', text: 'text-orange-600', darkText: 'dark:text-orange-200', border: 'border-orange-400', darkBorder: 'dark:border-orange-300', shadow: 'shadow-[0_0_12px_rgba(251,146,60,0.5)]', darkShadow: 'dark:shadow-[0_0_12px_rgba(253,186,116,0.4)]', badgeBg: 'bg-orange-50', badgeDarkBg: 'dark:bg-orange-900/30', badgeText: 'text-orange-600', badgeDarkText: 'dark:text-orange-200', badgeBorder: 'border-orange-200', badgeDarkBorder: 'dark:border-orange-500' },
                4: { light: 'bg-yellow-50', dark: 'dark:bg-yellow-950/30', text: 'text-yellow-800', darkText: 'dark:text-yellow-200', border: 'border-yellow-600', darkBorder: 'dark:border-yellow-500', shadow: 'shadow-[0_0_12px_rgba(202,138,4,0.5)]', darkShadow: 'dark:shadow-[0_0_12px_rgba(234,179,8,0.4)]', badgeBg: 'bg-yellow-100', badgeDarkBg: 'dark:bg-yellow-900/50', badgeText: 'text-yellow-800', badgeDarkText: 'dark:text-yellow-200', badgeBorder: 'border-yellow-400', badgeDarkBorder: 'dark:border-yellow-700' },
                5: { light: 'bg-yellow-50', dark: 'dark:bg-yellow-950/30', text: 'text-yellow-700', darkText: 'dark:text-yellow-300', border: 'border-yellow-500', darkBorder: 'dark:border-yellow-400', shadow: 'shadow-[0_0_12px_rgba(234,179,8,0.5)]', darkShadow: 'dark:shadow-[0_0_12px_rgba(250,204,21,0.4)]', badgeBg: 'bg-yellow-100', badgeDarkBg: 'dark:bg-yellow-900/50', badgeText: 'text-yellow-700', badgeDarkText: 'dark:text-yellow-300', badgeBorder: 'border-yellow-300', badgeDarkBorder: 'dark:border-yellow-600' },
                6: { light: 'bg-yellow-50', dark: 'dark:bg-yellow-950/30', text: 'text-yellow-600', darkText: 'dark:text-yellow-200', border: 'border-yellow-400', darkBorder: 'dark:border-yellow-300', shadow: 'shadow-[0_0_12px_rgba(250,204,21,0.5)]', darkShadow: 'dark:shadow-[0_0_12px_rgba(253,224,71,0.4)]', badgeBg: 'bg-yellow-50', badgeDarkBg: 'dark:bg-yellow-900/30', badgeText: 'text-yellow-600', badgeDarkText: 'dark:text-yellow-200', badgeBorder: 'border-yellow-200', badgeDarkBorder: 'dark:border-yellow-500' },
                7: { light: 'bg-yellow-50', dark: 'dark:bg-yellow-950/30', text: 'text-yellow-800', darkText: 'dark:text-yellow-200', border: 'border-yellow-600', darkBorder: 'dark:border-yellow-500', shadow: 'shadow-[0_0_12px_rgba(202,138,4,0.5)]', darkShadow: 'dark:shadow-[0_0_12px_rgba(234,179,8,0.4)]', badgeBg: 'bg-yellow-100', badgeDarkBg: 'dark:bg-yellow-900/50', badgeText: 'text-yellow-800', badgeDarkText: 'dark:text-yellow-200', badgeBorder: 'border-yellow-400', badgeDarkBorder: 'dark:border-yellow-700' },
                8: { light: 'bg-orange-50', dark: 'dark:bg-orange-950/30', text: 'text-orange-600', darkText: 'dark:text-orange-200', border: 'border-orange-400', darkBorder: 'dark:border-orange-300', shadow: 'shadow-[0_0_12px_rgba(251,146,60,0.5)]', darkShadow: 'dark:shadow-[0_0_12px_rgba(253,186,116,0.4)]', badgeBg: 'bg-orange-50', badgeDarkBg: 'dark:bg-orange-900/30', badgeText: 'text-orange-600', badgeDarkText: 'dark:text-orange-200', badgeBorder: 'border-orange-200', badgeDarkBorder: 'dark:border-orange-500' },
                9: { light: 'bg-orange-50', dark: 'dark:bg-orange-950/30', text: 'text-orange-700', darkText: 'dark:text-orange-300', border: 'border-orange-500', darkBorder: 'dark:border-orange-400', shadow: 'shadow-[0_0_12px_rgba(249,115,22,0.5)]', darkShadow: 'dark:shadow-[0_0_12px_rgba(251,146,60,0.4)]', badgeBg: 'bg-orange-100', badgeDarkBg: 'dark:bg-orange-900/50', badgeText: 'text-orange-700', badgeDarkText: 'dark:text-orange-300', badgeBorder: 'border-orange-300', badgeDarkBorder: 'dark:border-orange-600' },
                10: { light: 'bg-sky-50', dark: 'dark:bg-sky-950/30', text: 'text-sky-700', darkText: 'dark:text-sky-300', border: 'border-sky-500', darkBorder: 'dark:border-sky-400', shadow: 'shadow-[0_0_12px_rgba(14,165,233,0.5)]', darkShadow: 'dark:shadow-[0_0_12px_rgba(56,189,248,0.4)]', badgeBg: 'bg-sky-100', badgeDarkBg: 'dark:bg-sky-900/50', badgeText: 'text-sky-700', badgeDarkText: 'dark:text-sky-300', badgeBorder: 'border-sky-300', badgeDarkBorder: 'dark:border-sky-600' },
                11: { light: 'bg-blue-50', dark: 'dark:bg-blue-950/30', text: 'text-blue-700', darkText: 'dark:text-blue-300', border: 'border-blue-500', darkBorder: 'dark:border-blue-400', shadow: 'shadow-[0_0_12px_rgba(59,130,246,0.5)]', darkShadow: 'dark:shadow-[0_0_12px_rgba(96,165,250,0.4)]', badgeBg: 'bg-blue-100', badgeDarkBg: 'dark:bg-blue-900/50', badgeText: 'text-blue-700', badgeDarkText: 'dark:text-blue-300', badgeBorder: 'border-blue-300', badgeDarkBorder: 'dark:border-blue-600' },
              };
              
              const colors = seasonColors[idx as keyof typeof seasonColors];
              
              // Extrahera shadow-färg från colors
              const shadowMatch = colors.shadow.match(/rgba\([^)]+\)/);
              const shadowColor = shadowMatch ? shadowMatch[0] : 'rgba(59,130,246,0.5)';
              
              const isMonthSelected = selectedMonth === idx && !isFilterActive;
              return (
                <button
                  key={idx}
                  onClick={() => {
                    setSelectedMonth(idx);
                    setSearchQuery('');
                    setFilterTag('all');
                    setTimeout(() => {
                      scrollFiltersIntoView();
                    }, 200);
                  }}
                  className={`relative px-2 py-2.5 rounded-lg transition-all border-2 ${
                    isMonthSelected
                      ? `${colors.border} ${colors.darkBorder}` 
                      : 'border-gray-300 dark:border-gray-700'
                  }`}
                  style={{
                    backgroundColor: isMonthSelected ? colors.light.replace('bg-', '').replace(/^/, 'rgb(var(--') + ')' : 'transparent',
                    boxShadow: isMonthSelected ? `0 0 16px ${shadowColor.replace('0.5', '0.8')}` : 'none'
                  }}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <span className={`text-sm font-medium ${colors.text} ${colors.darkText}`}>{month.substring(0, 3)}</span>
                    <span
                      className={`
                        flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded border text-xs font-semibold
                        ${colors.badgeBg} ${colors.badgeDarkBg} ${colors.badgeText} ${colors.badgeDarkText} ${colors.badgeBorder} ${colors.badgeDarkBorder}
                      `}
                    >
                      {tasksInMonth.length}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Area filter, tag filter, search and hide-completed */}
          <div className="mt-4 space-y-2" ref={filterContainerRef}>
            <div className="flex items-center gap-2 flex-wrap">
              {hasGardeningTasks && (
                <button
                  type="button"
                  onClick={() => {
                    const nextArea: PrepArea | 'all' = filterArea === 'gardening' ? 'all' : 'gardening';
                    setFilterArea(nextArea);

                    // När Odlingsläget stängs av: nollställ tag-filter och sökfält, scrolla till toppen
                    if (nextArea !== 'gardening') {
                      setFilterTag('all');
                      setSearchQuery('');
                      // Fördröj scrollningen så att den vinner över eventuellt konkurrerande
                      // scroll-anrop (t.ex. scrollFiltersIntoView) som annars körs i samband.
                      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
                    }

                    // På desktop: dölj vänstermenyn i Odlingsläget och visa den igen när läget stängs av
                    try {
                      if (typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches) {
                        const shouldCollapse = nextArea === 'gardening';
                        window.dispatchEvent(new CustomEvent<boolean>('desktop-sidebar-set', { detail: shouldCollapse }));
                      }
                    } catch {
                      // Ignorera eventuella fel i miljö utan window
                    }

                    if (nextArea === 'gardening') {
                      scrollFiltersIntoView();
                    }
                  }}
                  className={`flex items-center justify-center w-9 h-9 rounded-lg border transition-colors shadow-sm mr-1
                    ${
                      filterArea === 'gardening'
                        ? 'border-lime-300 bg-lime-500/20 text-lime-200 shadow-[0_0_8px_rgba(190,242,100,0.7)]'
                        : 'border-border bg-input text-lime-300 hover:bg-lime-900/30'
                    }
                  `}
                  title={
                    filterArea === 'gardening'
                      ? 'Visa alla områden'
                      : 'Snabbfilter: visa bara Odla'
                  }
                  aria-label={
                    filterArea === 'gardening'
                      ? 'Stäng Odla-filter (visa alla områden)'
                      : 'Aktivera Odla-filter'
                  }
                >
                  <Sprout className="h-4 w-4" />
                </button>
              )}
              <AreaFilterDropdown 
                value={filterArea}
                onChange={(area) => {
                  setFilterArea(area);
                  scrollFiltersIntoView();
                }}
                areaConfig={areaConfig}
                isActive={filterArea !== 'all'}
              />
              <TagFilterDropdown 
                value={filterTag}
                onChange={(tag) => {
                  setFilterTag(tag);
                  // Clear search när man filtrerar på tagg
                  if (tag !== 'all') {
                    setSearchQuery('');
                  }
                  scrollFiltersIntoView();
                }}
                allTags={allTags}
                isActive={filterTag !== 'all'}
              />
              <div className="relative w-full sm:flex-1 sm:w-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    // Clear tag filter when searching
                    if (e.target.value.trim()) {
                      setFilterTag('all');
                    }
                  }}
                  placeholder="Sök uppgifter..."
                  className={`w-full pl-10 pr-10 py-2 border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
                    searchQuery.trim()
                      ? 'border-slate-300 shadow-[0_0_8px_rgba(148,163,184,0.6)]'
                      : 'border-border'
                  }`}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground z-10"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            {/* Dölj avklarade checkbox – alltid egen rad */}
            <div className="flex">
              <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-muted-foreground hover:text-foreground transition-colors">
                <input
                  type="checkbox"
                  checked={hideCompleted}
                  onChange={(e) => setHideCompleted(e.target.checked)}
                  className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                />
                Dölj avklarade
              </label>
            </div>

            {/* Desktop-only quick tags for gardening mode */}
            {filterArea === 'gardening' && gardeningTags.length > 0 && (
              <div className="hidden md:flex flex-wrap gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setFilterTag('all');
                    scrollFiltersIntoView();
                  }}
                  className="px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-all"
                  style={
                    filterTag === 'all'
                      ? {
                          backgroundColor: 'rgba(132, 204, 22, 0.20)', // lime-500/20
                          color: '#d9f99d', // lime-200
                          border: '2px solid #bef264', // lime-300
                          boxShadow: '0 0 15px rgba(190, 242, 100, 0.7), 0 0 25px rgba(132, 204, 22, 0.6)',
                        }
                      : {
                          backgroundColor: 'rgba(23, 37, 23, 0.85)',
                          color: '#bef264',
                          border: '1px solid rgba(190, 242, 100, 0.35)',
                        }
                  }
                  onMouseEnter={(e) => {
                    if (filterTag !== 'all') {
                      e.currentTarget.style.backgroundColor = 'rgba(63, 98, 18, 0.9)';
                      e.currentTarget.style.borderColor = 'rgba(190, 242, 100, 0.7)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (filterTag !== 'all') {
                      e.currentTarget.style.backgroundColor = 'rgba(23, 37, 23, 0.85)';
                      e.currentTarget.style.borderColor = 'rgba(190, 242, 100, 0.35)';
                    }
                  }}
                >
                  Alla odlingstaggar
                </button>

                {gardeningTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      const next = filterTag === tag ? 'all' : tag;
                      setFilterTag(next);
                      scrollFiltersIntoView();
                    }}
                    className="px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-all"
                    style={
                      filterTag === tag
                        ? {
                            backgroundColor: 'rgba(132, 204, 22, 0.20)',
                            color: '#d9f99d',
                            border: '2px solid #bef264',
                            boxShadow: '0 0 15px rgba(190, 242, 100, 0.7), 0 0 25px rgba(132, 204, 22, 0.6)',
                          }
                        : {
                            backgroundColor: 'rgba(23, 37, 23, 0.85)',
                            color: '#bef264',
                            border: '1px solid rgba(190, 242, 100, 0.35)',
                          }
                    }
                    onMouseEnter={(e) => {
                      if (filterTag !== tag) {
                        e.currentTarget.style.backgroundColor = 'rgba(63, 98, 18, 0.9)';
                        e.currentTarget.style.borderColor = 'rgba(190, 242, 100, 0.7)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (filterTag !== tag) {
                        e.currentTarget.style.backgroundColor = 'rgba(23, 37, 23, 0.85)';
                        e.currentTarget.style.borderColor = 'rgba(190, 242, 100, 0.35)';
                      }
                    }}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      {viewMode === 'wheel' && !searchQuery.trim() && filterTag === 'all' && (
        <div
          className="space-y-4"
          style={{
            minHeight: 'calc(100vh - 5rem)',
            paddingBottom: 'calc(100vh - 5rem)',
          }}
        >
          <div ref={monthHeadingRef} className="flex items-center justify-between">
            <h2
              className="text-xl font-semibold text-foreground cursor-pointer select-none"
              onClick={goToCurrentMonth}
              title="Klicka för att gå till aktuell månad"
            >
              {filterArea === 'gardening' ? 'Odlingskalender - ' : ''}
              {months[selectedMonth]} {selectedYear}
            </h2>
            
            {/* Column Layout Switcher (hidden on mobile) */}
            <div className="hidden md:flex items-center gap-1 bg-muted rounded-lg p-1">
              <button
                onClick={() => setGridColumns('gallery')}
                className={`p-2 rounded transition-colors ${
                  gridColumns === 'gallery'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                title="Gallerivy (3 kolumner)"
              >
                <Grid3x3 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setGridColumns('single')}
                className={`p-2 rounded transition-colors ${
                  gridColumns === 'single'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                title="Listvy (1 kolumn)"
              >
                <Rows3 className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          {monthTasks.length === 0 ? (
            <div className="bg-card rounded-xl p-8 text-center border border-border">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Check className="h-6 w-6 text-primary" />
              </div>
              <p className="text-muted-foreground">
                {t('yearWheel.noTasksThisMonth', 'Inga uppgifter planerade denna månad')}
              </p>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden relative">
              {filterArea === 'gardening' && (
                <div className="absolute inset-0 overflow-hidden" style={{ transform: 'scale(1.03)' }}>
                  {gardenBgTiles.map((tile, i) => (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        left: 0, right: 0,
                        top: `${tile.topVh}vh`,
                        height: `${tile.heightVh}vh`,
                        backgroundImage: "url('/yearwheel/garden.png')",
                        backgroundSize: 'cover',
                        backgroundPosition: `center ${tile.posY}%`,
                        filter: 'brightness(0.65) blur(4px)',
                      }}
                    />
                  ))}
                </div>
              )}
              <div className="relative p-2 sm:p-3 md:p-4">
                <div className={gridColumns === 'single' ? 'grid gap-4' : 'grid gap-4 sm:grid-cols-2 2xl:grid-cols-3'}>
                  {monthTasks.map(task => (
                    <TaskCard 
                      key={task.id} 
                      task={task} 
                      onClick={(e) => {
                        e.preventDefault();
                        setShowTaskModal(task);
                      }}
                      onCopy={() => {
                        setCopyTaskData(task);
                        setShowAddModal(true);
                      }}
                      onAddToShoppingList={openShoppingModal}
                      isAlreadyInShoppingList={shoppingItems.some(item => item.name === task.title)}
                      getDaysUntil={getDaysUntil}
                      getStatusColor={getStatusColor}
                      displayedMonth={selectedMonth}
                      displayedYear={selectedYear}
                      isListView={gridColumns === 'single'}
                      isGardeningFilterActive={filterArea === 'gardening'}
                      onTagClick={(tag) => {
                        setFilterTag(tag);
                        setTimeout(() => {
                          scrollFiltersIntoView();
                        }, 200);
                      }}
                      onAreaClick={(area) => {
                        setFilterArea(area);
                        setTimeout(() => {
                          scrollFiltersIntoView();
                        }, 200);
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {viewMode === 'overdue' && !searchQuery.trim() && filterTag === 'all' && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-red-400">
            {t('yearWheel.overdueTasksTitle', 'Försenade uppgifter')}
          </h2>
          
          {overdueTasks.length === 0 ? (
            <div className="bg-primary/10 rounded-xl p-8 text-center border border-primary/30">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Check className="h-6 w-6 text-primary" />
              </div>
              <p className="text-primary font-medium">
                {t('yearWheel.noOverdue', 'Fantastiskt! Inga försenade uppgifter!')}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3">
              {overdueTasks.map(task => (
                <TaskCard 
                  key={task.id} 
                  task={task} 
                  onClick={(e) => {
                    e.preventDefault();
                    setShowTaskModal(task);
                  }}
                  onCopy={() => {
                    setCopyTaskData(task);
                    setShowAddModal(true);
                  }}
                  onAddToShoppingList={openShoppingModal}
                  isAlreadyInShoppingList={shoppingItems.some(item => item.name === task.title)}
                  getDaysUntil={getDaysUntil}
                  getStatusColor={getStatusColor}
                  isOverdue
                  displayedMonth={new Date(task.nextDue).getMonth()}
                  displayedYear={new Date(task.nextDue).getFullYear()}
                  onTagClick={(tag) => {
                    setFilterTag(tag);
                    setTimeout(() => {
                      scrollFiltersIntoView();
                    }, 200);
                  }}
                  onAreaClick={(area) => {
                    setFilterArea(area);
                    setTimeout(() => {
                      scrollFiltersIntoView();
                    }, 200);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search Results */}
      {searchQuery.trim() && filterTag === 'all' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-foreground">
              Sökresultat för "{searchQuery}"
            </h2>
            <span className="text-sm text-muted-foreground">
              {searchResults.length} {searchResults.length === 1 ? 'träff' : 'träffar'}
            </span>
          </div>
          
          {searchResults.length === 0 ? (
            <div className="bg-card rounded-xl p-8 text-center border border-border">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Search className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                Inga uppgifter matchar din sökning
              </p>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden relative">
              {filterArea === 'gardening' && (
                <div className="absolute inset-0 overflow-hidden" style={{ transform: 'scale(1.03)' }}>
                  {gardenBgTiles.map((tile, i) => (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        left: 0, right: 0,
                        top: `${tile.topVh}vh`,
                        height: `${tile.heightVh}vh`,
                        backgroundImage: "url('/yearwheel/garden.png')",
                        backgroundSize: 'cover',
                        backgroundPosition: `center ${tile.posY}%`,
                        filter: 'brightness(0.65) blur(4px)',
                      }}
                    />
                  ))}
                </div>
              )}
              <div className="relative p-2 sm:p-3 md:p-4 space-y-6">
                {Object.keys(groupedSearchResults)
                  .sort((a, b) => {
                    const [yearA, monthA] = a.split('-').map(Number);
                    const [yearB, monthB] = b.split('-').map(Number);
                    return yearA * 12 + monthA - (yearB * 12 + monthB);
                  })
                  .map((key) => {
                    const [year, month] = key.split('-').map(Number);
                    const monthResults = groupedSearchResults[key];
                    
                    return (
                      <div key={key} className="space-y-3">
                        <h3
                          className={`text-lg font-semibold text-foreground flex items-center gap-2 ${
                            filterArea === 'gardening'
                              ? 'bg-card/95 border border-border/80 rounded-lg px-3 py-2 shadow-sm'
                              : ''
                          }`}
                        >
                          <CalendarDays className="h-5 w-5 text-primary" />
                          {months[month]} {year}
                        </h3>
                        <div className="space-y-3">
                          {monthResults.map((result, idx) => (
                            <TaskCard 
                              key={`${result.task.id}-${month}-${year}-${idx}`}
                              task={result.task}
                              onClick={(e) => {
                                e.preventDefault();
                                setShowTaskModal(result.task);
                              }}
                              onCopy={() => {
                                setCopyTaskData(result.task);
                                setShowAddModal(true);
                              }}
                              onAddToShoppingList={openShoppingModal}
                              isAlreadyInShoppingList={shoppingItems.some(item => item.name === result.task.title)}
                              getDaysUntil={getDaysUntil}
                              getStatusColor={getStatusColor}
                              displayedMonth={month}
                              displayedYear={year}
                              isGardeningFilterActive={filterArea === 'gardening'}
                              onTagClick={(tag) => {
                                setFilterTag(tag);
                                setTimeout(() => {
                                  scrollFiltersIntoView();
                                }, 200);
                              }}
                              onAreaClick={(area) => {
                                setFilterArea(area);
                                setTimeout(() => {
                                  scrollFiltersIntoView();
                                }, 200);
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tag Filter Results */}
      {filterTag !== 'all' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-foreground">
              Uppgifter med tagg: <span className="text-primary">{filterTag}</span>
            </h2>
            <span className="text-sm text-muted-foreground">
              {tagFilterResults.length} {tagFilterResults.length === 1 ? 'träff' : 'träffar'}
            </span>
          </div>
          
          {tagFilterResults.length === 0 ? (
            <div className="bg-card rounded-xl p-8 text-center border border-border">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Search className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                Inga uppgifter med denna tagg
              </p>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden relative">
              {filterArea === 'gardening' && (
                <div className="absolute inset-0 overflow-hidden" style={{ transform: 'scale(1.03)' }}>
                  {gardenBgTiles.map((tile, i) => (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        left: 0, right: 0,
                        top: `${tile.topVh}vh`,
                        height: `${tile.heightVh}vh`,
                        backgroundImage: "url('/yearwheel/garden.png')",
                        backgroundSize: 'cover',
                        backgroundPosition: `center ${tile.posY}%`,
                        filter: 'brightness(0.65) blur(4px)',
                      }}
                    />
                  ))}
                </div>
              )}
              <div className="relative p-2 sm:p-3 md:p-4 space-y-6">
                {Object.keys(groupedTagFilterResults)
                  .sort((a, b) => {
                    const [yearA, monthA] = a.split('-').map(Number);
                    const [yearB, monthB] = b.split('-').map(Number);
                    return yearA * 12 + monthA - (yearB * 12 + monthB);
                  })
                  .map((key) => {
                    const [year, month] = key.split('-').map(Number);
                    const monthResults = groupedTagFilterResults[key];
                    
                    return (
                      <div key={key} className="space-y-3">
                        <h3
                          className={`text-lg font-semibold text-foreground flex items-center gap-2 ${
                            filterArea === 'gardening'
                              ? 'bg-card/95 border border-border/80 rounded-lg px-3 py-2 shadow-sm'
                              : ''
                          }`}
                        >
                          <CalendarDays className="h-5 w-5 text-primary" />
                          {months[month]} {year}
                        </h3>
                        <div className="space-y-3">
                          {monthResults.map((result, idx) => (
                            <TaskCard 
                              key={`${result.task.id}-${month}-${year}-${idx}`}
                              task={result.task}
                              onClick={(e) => {
                                e.preventDefault();
                                setShowTaskModal(result.task);
                              }}
                              onCopy={() => {
                                setCopyTaskData(result.task);
                                setShowAddModal(true);
                              }}
                              onAddToShoppingList={openShoppingModal}
                              isAlreadyInShoppingList={shoppingItems.some(item => item.name === result.task.title)}
                              getDaysUntil={getDaysUntil}
                              getStatusColor={getStatusColor}
                              displayedMonth={month}
                              displayedYear={year}
                              isGardeningFilterActive={filterArea === 'gardening'}
                              onTagClick={(tag) => {
                                setFilterTag(tag);
                                setTimeout(() => {
                                  scrollFiltersIntoView();
                                }, 200);
                              }}
                              onAreaClick={(area) => {
                                setFilterArea(area);
                                setTimeout(() => {
                                  scrollFiltersIntoView();
                                }, 200);
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Shopping List Modal */}
      {showShoppingModal && selectedTaskForShopping && (
        <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/20">
                  <ShoppingCart className="h-6 w-6 text-primary" />
                </div>
                <h2 className="text-xl font-semibold">Lägg till i inköpslista</h2>
              </div>
              <button
                onClick={closeShoppingModal}
                className="p-1 rounded hover:bg-muted flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-1">Artikel</p>
              <p className="font-medium">{selectedTaskForShopping.title}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Kategori</label>
              <select
                value={shoppingCategory}
                onChange={(e) => setShoppingCategory(e.target.value as InventoryCategory)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background"
              >
                <option value="mat">{CATEGORY_LABELS.mat}</option>
                <option value="medicin">{CATEGORY_LABELS.medicin}</option>
                <option value="hygien">{CATEGORY_LABELS.hygien}</option>
                <option value="verktyg">{CATEGORY_LABELS.verktyg}</option>
                <option value="kommunikation">{CATEGORY_LABELS.kommunikation}</option>
                <option value="varme">{CATEGORY_LABELS.varme}</option>
                <option value="ljus">{CATEGORY_LABELS.ljus}</option>
                <option value="dokument">{CATEGORY_LABELS.dokument}</option>
                <option value="ovrigt">{CATEGORY_LABELS.ovrigt}</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Beskrivning (valfritt)
                <span className="ml-2 text-xs text-muted-foreground">{shoppingNotes.length}/200</span>
              </label>
              <textarea
                value={shoppingNotes}
                maxLength={200}
                onChange={(e) => setShoppingNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background resize-none"
                placeholder="T.ex. ekologisk, glutenfri…"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Antal</label>
              <div className="flex items-center gap-2 max-w-xs">
                <button
                  onClick={() => setShoppingQuantity(Math.max(1, shoppingQuantity - 1))}
                  className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={shoppingQuantity}
                  onChange={(e) => setShoppingQuantity(Math.min(1000, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="w-20 px-3 py-2 rounded-lg border border-border bg-background text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  onClick={() => setShoppingQuantity(Math.min(1000, shoppingQuantity + 1))}
                  className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Pris (valfritt)</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShoppingPrice(prev => { const cur = parseFloat((prev || '0').replace(',', '.')) || 0; const n = Math.max(0, cur - 100); return n % 1 === 0 ? String(n) : String(n).replace('.', ','); })}
                  disabled={parseFloat((shoppingPrice || '0').replace(',', '.')) <= 0}
                  className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={shoppingPrice}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9,.]/g, '').replace('.', ',').replace(/,{2,}/g, ',');
                      const parts = raw.split(',');
                      const cleaned = parts[0].slice(0, 7) + (parts.length > 1 ? ',' + parts[1].slice(0, 2) : '');
                      if (cleaned === '' || cleaned === ',') { setShoppingPrice('0'); return; }
                      const numVal = parseFloat(cleaned.replace(',', '.'));
                      if (!isNaN(numVal) && numVal <= 1000000) setShoppingPrice(cleaned);
                    }}
                    className="w-24 px-3 py-2 text-center rounded-lg border border-border bg-background"
                  />
                  <span className="text-sm text-muted-foreground">kr</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShoppingPrice(prev => { const cur = parseFloat((prev || '0').replace(',', '.')) || 0; const n = Math.min(1000000, cur + 100); return n % 1 === 0 ? String(n) : String(n).replace('.', ','); })}
                  className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Butik (valfritt)
                <span className="ml-2 text-xs text-muted-foreground">{shoppingStore.length}/50</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  maxLength={50}
                  value={shoppingStore}
                  onChange={(e) => setShoppingStore(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder="T.ex. ICA Maxi"
                />
                <button
                  type="button"
                  onClick={() => {
                    setStoreSuggestionSearch('');
                    setShowStoreSuggestions(true);
                  }}
                  className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  title="Visa befintliga butiker"
                >
                  <Search className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Hylla/Avdelning (valfritt)
                <span className="ml-2 text-xs text-muted-foreground">{shoppingAisle.length}/50</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  maxLength={50}
                  value={shoppingAisle}
                  onChange={(e) => setShoppingAisle(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder="T.ex. Mejeri"
                />
                <button
                  type="button"
                  onClick={() => {
                    setAisleSuggestionSearch('');
                    setShowAisleSuggestions(true);
                  }}
                  className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  title="Visa avdelningar"
                >
                  <Search className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Välj inköpslista</label>
              {showNewListForm ? (
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">{newListName.length}/25</span>
                    </div>
                    <input
                      type="text"
                      maxLength={25}
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      placeholder="Namn på ny lista"
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary focus:border-transparent"
                      autoFocus
                    />
                  </div>
                  <button
                    onClick={() => {
                      setShowNewListForm(false);
                      setNewListName('');
                      const primary = shoppingLists.find(l => l.isPrimary);
                      setSelectedListId(primary?.id || shoppingLists[0]?.id || null);
                    }}
                    className="text-sm text-primary hover:underline"
                  >
                    ← Tillbaka till befintliga listor
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {shoppingLists.length > 0 && (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {shoppingLists.map(list => (
                        <label
                          key={list.id}
                          className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                        >
                          <input
                            type="radio"
                            name="yw-shopping-list"
                            checked={selectedListId === list.id}
                            onChange={() => setSelectedListId(list.id)}
                            className="text-primary focus:ring-primary"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium break-words [overflow-wrap:anywhere] [hyphens:auto]">{list.name}</span>
                              {list.isPrimary && (
                                <span className="text-yellow-500" title="Primär lista">★</span>
                              )}
                            </div>
                            {list.store && (
                              <span className="text-xs text-muted-foreground">{list.store}</span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => setShowNewListForm(true)}
                    className="w-full px-4 py-2 rounded-lg border border-dashed border-border transition-colors text-sm flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#67724e', color: '#fff' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#56633f')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#67724e')}
                  >
                    <Plus className="h-4 w-4" />
                    Skapa ny inköpslista
                  </button>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeShoppingModal}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={async () => {
                  if (showNewListForm) {
                    if (!newListName.trim()) return;
                    const newList = await createShoppingList({ name: newListName, isPrimary: shoppingLists.length === 0 });
                    if (newList && selectedTaskForShopping) {
                      await createShoppingItem({
                        listId: newList.id,
                        name: selectedTaskForShopping.title,
                        category: CATEGORY_LABELS[shoppingCategory],
                        notes: shoppingNotes || undefined,
                        quantity: shoppingQuantity,
                        price: parseFloat((shoppingPrice || '0').replace(',', '.')) || 0,
                        store: shoppingStore || undefined,
                        aisle: shoppingAisle || undefined,
                      });
                    }
                  } else {
                    if (!selectedListId || !selectedTaskForShopping) return;
                    await createShoppingItem({
                      listId: selectedListId,
                      name: selectedTaskForShopping.title,
                      category: CATEGORY_LABELS[shoppingCategory],
                      notes: shoppingNotes || undefined,
                      quantity: shoppingQuantity,
                      price: parseFloat((shoppingPrice || '0').replace(',', '.')) || 0,
                      store: shoppingStore || undefined,
                      aisle: shoppingAisle || undefined,
                    });
                  }
                  closeShoppingModal();
                }}
                disabled={showNewListForm ? !newListName.trim() : !selectedListId}
                className="flex-1 px-4 py-2 rounded-lg btn-primary-custom transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Lägg till
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Store Suggestions Modal (Shopping) */}
      {showStoreSuggestions && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj butik</h3>
              <button onClick={() => setShowStoreSuggestions(false)} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={storeSuggestionSearch}
                  onChange={(e) => setStoreSuggestionSearch(e.target.value)}
                  placeholder="Sök butik..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {Array.from(new Set(shoppingItems.filter(i => i.store).map(i => i.store as string)))
                .filter(s => s.toLowerCase().includes(storeSuggestionSearch.toLowerCase()))
                .map(store => (
                  <button
                    key={store}
                    onClick={() => { setShoppingStore(store); setShowStoreSuggestions(false); }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                  >
                    {store}
                  </button>
                ))}
              {shoppingItems.filter(i => i.store).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">Inga sparade butiker</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Aisle Suggestions Modal (Shopping) */}
      {showAisleSuggestions && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj avdelning</h3>
              <button onClick={() => setShowAisleSuggestions(false)} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={aisleSuggestionSearch}
                  onChange={(e) => setAisleSuggestionSearch(e.target.value)}
                  placeholder="Sök avdelning..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {(CATEGORY_AISLES[shoppingCategory] || COMMON_AISLES_YW)
                .filter(a => a.toLowerCase().includes(aisleSuggestionSearch.toLowerCase()))
                .map(aisle => (
                  <button
                    key={aisle}
                    onClick={() => { setShoppingAisle(aisle); setShowAisleSuggestions(false); }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                  >
                    {aisle}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {showTaskModal && (
        <TaskDetailModal
          task={showTaskModal}
          onClose={() => {
            setShowTaskModal(null);
          }}
          onRewind={() => handleRewindTask(showTaskModal)}
          onComplete={() => handleCompleteTask(showTaskModal)}
          onEdit={() => {
            // Markera att vi är i en modal-övergång
            isTransitioningModalsRef.current = true;
            setShowEditModal(showTaskModal);
            setShowTaskModal(null);
            // Återställ flaggan efter en kort delay
            setTimeout(() => {
              isTransitioningModalsRef.current = false;
            }, 100);
          }}
          onDelete={async () => {
            try {
              await deleteTask(showTaskModal.id);
              // Vänta lite så React hinner uppdatera state
              await new Promise(resolve => setTimeout(resolve, 50));
              setShowTaskModal(null);
            } catch (error) {
              console.error('Kunde inte radera uppgift:', error);
              setShowTaskModal(null);
            }
          }}
          getDaysUntil={getDaysUntil}
          onShowPremiumModal={() => {
            setShowTaskModal(null);
            setShowPremiumModal(true);
          }}
        />
      )}

      {/* Add Task Modal */}
      {showAddModal && (
        <AddTaskModal
          initialMonth={selectedMonth}
          initialData={copyTaskData ?? undefined}
          availableTags={allTags}
          initialArea={filterArea === 'all' ? 'water' : filterArea}
          onClose={() => {
            setShowAddModal(false);
            setCopyTaskData(null);
          }}
          onAdd={async (task) => {
            await addTask(task);
            setShowAddModal(false);
            setCopyTaskData(null);
          }}
        />
      )}

      {/* Edit Task Modal */}
      {showEditModal && (
        <EditTaskModal
          task={showEditModal}
          availableTags={allTags}
          onClose={() => transitionFromEditModalToTaskModal(showEditModal)}
          onSave={async (updates) => {
            await updateTask(showEditModal.id, updates);

            const updatedTask = useYearWheelStore
              .getState()
              .tasks.find((task) => task.id === showEditModal.id);

            transitionFromEditModalToTaskModal(updatedTask ?? { ...showEditModal, ...updates });
          }}
        />
      )}

      {/* Premium Required Modal */}
      <PremiumRequiredModal
        open={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature="Årshjulet"
      />

      {/* CSV Import Modal */}
      {showCSVImportModal && (
        <CSVImportModal
          onClose={() => setShowCSVImportModal(false)}
        />
      )}

      {/* CSV Export Modal */}
      {showCSVExportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-border">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <FileDown className="h-6 w-6 text-primary" />
                <h2 className="text-xl font-semibold text-foreground">Exportera till CSV</h2>
              </div>
              <button
                onClick={() => setShowCSVExportModal(false)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Areas */}
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-foreground">Områden</h3>
                  <div className="flex items-center gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => setExportAreas(Object.keys(areaConfig) as PrepArea[])}
                      className="px-2 py-1 rounded border border-border hover:bg-muted transition-colors"
                    >
                      Markera alla
                    </button>
                    <button
                      type="button"
                      onClick={() => setExportAreas([])}
                      className="px-2 py-1 rounded border border-border hover:bg-muted transition-colors"
                    >
                      Avmarkera alla
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {(Object.keys(areaConfig) as PrepArea[]).map((areaKey) => {
                    const cfg = areaConfig[areaKey];
                    const checked = exportAreas.includes(areaKey);
                    return (
                      <label
                        key={areaKey}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted/40 hover:bg-muted cursor-pointer text-xs sm:text-sm"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-border"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setExportAreas(prev => prev.includes(areaKey) ? prev : [...prev, areaKey]);
                            } else {
                              setExportAreas(prev => prev.filter(a => a !== areaKey));
                            }
                          }}
                        />
                        <span className="flex items-center gap-1">
                          <cfg.icon className={`h-4 w-4 ${cfg.iconColor}`} />
                          <span>{cfg.name}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Tags */}
              {allTags.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-foreground">Taggar</h3>
                    <div className="flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => setExportTags(allTags)}
                        className="px-2 py-1 rounded border border-border hover:bg-muted transition-colors"
                      >
                        Markera alla
                      </button>
                      <button
                        type="button"
                        onClick={() => setExportTags([])}
                        className="px-2 py-1 rounded border border-border hover:bg-muted transition-colors"
                      >
                        Avmarkera alla
                      </button>
                    </div>
                  </div>
                  <div className="max-h-56 overflow-y-auto rounded-lg border border-border bg-muted/40 p-3 flex flex-wrap gap-2">
                    {allTags.map((tag) => {
                      const checked = exportTags.includes(tag);
                      return (
                        <label
                          key={tag}
                          className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-background text-xs border border-border hover:bg-muted cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-border"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setExportTags(prev => prev.includes(tag) ? prev : [...prev, tag]);
                              } else {
                                setExportTags(prev => prev.filter(t => t !== tag));
                              }
                            }}
                          />
                          <span>{tag}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <button
                onClick={() => setShowCSVExportModal(false)}
                className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  const selectedAreas = exportAreas.length > 0 ? new Set(exportAreas) : new Set(Object.keys(areaConfig) as PrepArea[]);
                  const selectedTags = exportTags.length > 0 ? new Set(exportTags) : new Set(allTags);

                  let tasksToExport = tasks.filter(task => selectedAreas.has(task.area));

                  if (allTags.length > 0 && exportTags.length > 0 && exportTags.length !== allTags.length) {
                    tasksToExport = tasksToExport.filter(task => {
                      if (!task.tags || task.tags.length === 0) return false;
                      return task.tags.some(tag => selectedTags.has(tag));
                    });
                  }

                  handleExportCSV(tasksToExport);
                  setShowCSVExportModal(false);
                }}
                className="px-4 py-2 rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#67724e' }}
                disabled={exportAreas.length === 0 && tasks.length > 0 && Object.keys(areaConfig).length > 0}
              >
                Exportera
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flytande månadsnavigering + Lägg till-knapp */}
      {!showAddModal && !showTaskModal && !showEditModal && !showPremiumModal && !showCSVImportModal && !showCSVExportModal && !hideFab && (
        <div className="fixed top-[3.75rem] right-4 z-50 flex items-center gap-2">
          <button
            onClick={goToPreviousMonthFromFab}
            disabled={!canNavigateToPreviousMonth}
            className="inline-flex items-center justify-center rounded-full p-3 shadow-lg text-foreground hover:scale-105 transition-transform disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{ borderRadius: '9999px', backgroundColor: 'color-mix(in srgb, hsl(var(--input)) 82%, white 18%)' }}
            title="Föregående månad"
            aria-label="Gå till föregående månad"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={goToNextMonthFromFab}
            disabled={!canNavigateToNextMonth}
            className="inline-flex items-center justify-center rounded-full p-3 shadow-lg text-foreground hover:scale-105 transition-transform disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{ borderRadius: '9999px', backgroundColor: 'color-mix(in srgb, hsl(var(--input)) 82%, white 18%)' }}
            title="Nästa månad"
            aria-label="Gå till nästa månad"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <button
            onClick={openAddTaskModal}
            className="rounded-full p-3 shadow-lg btn-primary-custom hover:scale-105 transition-transform"
            style={{ borderRadius: '9999px' }}
            title="Lägg till uppgift"
            aria-label="Lägg till uppgift"
          >
            <Plus className="h-5 w-5 text-white" />
          </button>
        </div>
      )}
    </div>
  );
};

// Helper: check if a task's occurrence for [targetMonth, targetYear] is completed.
// Extracted to share logic between TaskCard rendering and the hideCompleted filter.
function isTaskCompletedForPeriod(task: RecurringTask, targetMonth: number, targetYear: number): boolean {
  if (!task.lastCompleted) return false;

  const createdYear = new Date(task.createdAt).getFullYear();
  const createdMonth = new Date(task.createdAt).getMonth();
  if (targetYear < createdYear) return false;
  if (targetYear === createdYear && targetMonth < createdMonth) return false;

  const baseMonth = task.month;

  const parseYmd = (value: string): Date | null => {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  const getIntervalMonths = (frequency: TaskFrequency): number => getFrequencyIntervalMonths(frequency);

  let occursThisMonth = false;
  switch (task.frequency) {
    case 'monthly':
      occursThisMonth = targetYear >= createdYear;
      break;
    case 'quarterly':
      occursThisMonth = (targetMonth - baseMonth + 12) % 3 === 0 && targetYear >= createdYear;
      break;
    case 'biannual': {
      const secondMonth = (baseMonth + 6) % 12;
      occursThisMonth = (targetMonth === baseMonth || targetMonth === secondMonth) && targetYear >= createdYear;
      break;
    }
    case 'annual':
      occursThisMonth = targetMonth === baseMonth && targetYear >= createdYear;
      break;
    default:
      occursThisMonth = false;
  }

  if (!occursThisMonth) return false;

  const intervalMonths = getIntervalMonths(task.frequency);

  const nextDueDate = parseYmd(task.nextDue);
  if (!nextDueDate) return false;

  const previousOccurrenceMonth = new Date(
    nextDueDate.getFullYear(),
    nextDueDate.getMonth() - intervalMonths,
    1,
  );

  const completedOccurrenceDue = getOccurrenceDueDate(
    previousOccurrenceMonth.getFullYear(),
    previousOccurrenceMonth.getMonth(),
    task.deadlineType || DEFAULT_TASK_DEADLINE,
  );

  const displayedOccurrenceDue = getOccurrenceDueDate(
    targetYear,
    targetMonth,
    task.deadlineType || DEFAULT_TASK_DEADLINE,
  );


  return displayedOccurrenceDue.getTime() <= completedOccurrenceDue.getTime();
}

// Task Card Component
interface TaskCardProps {
  task: RecurringTask;
  onClick: (e: React.MouseEvent) => void;
  onCopy: () => void;
  onAddToShoppingList?: (task: RecurringTask) => void;
  isAlreadyInShoppingList?: boolean;
  getDaysUntil: (date: string) => number;
  getStatusColor: (date: string) => string;
  isOverdue?: boolean;
  displayedMonth?: number;
  displayedYear?: number;
  isListView?: boolean;
  onTagClick?: (tag: string) => void;
  onAreaClick?: (area: PrepArea) => void;
  isGardeningFilterActive?: boolean;
}
const TaskCard: React.FC<TaskCardProps> = ({ task, onClick, onCopy, onAddToShoppingList, isAlreadyInShoppingList, getDaysUntil, getStatusColor, isOverdue, displayedMonth, displayedYear, isListView, onTagClick, onAreaClick, isGardeningFilterActive }) => {
  const days = getDaysUntil(task.nextDue);
  const area = areaConfig[task.area];
  const Icon = area.icon;
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const parseYmd = (value: string): Date | null => {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  const getIntervalMonths = (frequency: TaskFrequency): number => getFrequencyIntervalMonths(frequency);
  
  // Determine if the task should be considered completed for the displayed period
  const completed = (() => {
    if (!task.lastCompleted) return false;

    // Determine target month/year (from displayed props or nextDue)
    const targetYear = typeof displayedYear === 'number' ? displayedYear : new Date(task.nextDue).getFullYear();
    const targetMonth = typeof displayedMonth === 'number' ? displayedMonth : new Date(task.nextDue).getMonth();

    // Ensure this is a scheduled occurrence for the task
    const createdYear = new Date(task.createdAt).getFullYear();
    if (targetYear < createdYear) return false;
    if (targetYear === createdYear && targetMonth < new Date(task.createdAt).getMonth()) return false;

    let occursThisMonth = false;
    const baseMonth = task.month;
    switch (task.frequency) {
      case 'monthly':
        occursThisMonth = targetYear >= createdYear;
        break;
      case 'quarterly':
        occursThisMonth = (targetMonth - baseMonth + 12) % 3 === 0 && targetYear >= createdYear;
        break;
      case 'biannual': {
        const secondMonth = (baseMonth + 6) % 12;
        occursThisMonth = (targetMonth === baseMonth || targetMonth === secondMonth) && targetYear >= createdYear;
        break;
      }
      case 'annual':
        occursThisMonth = targetMonth === baseMonth && targetYear >= createdYear;
        break;
      default:
        occursThisMonth = false;
    }

    if (!occursThisMonth) return false;

    const intervalMonths = getIntervalMonths(task.frequency);

    // En slutförd occurrence representeras av månaden precis före task.nextDue.
    // Detta gör att sena slutföranden (t.ex. feb-uppgift klar i mars) ändå markerar
    // den korrekta februari-occurencen som slutförd.
    const nextDueDate = parseYmd(task.nextDue);
    if (!nextDueDate) return false;

    const previousOccurrenceMonth = new Date(
      nextDueDate.getFullYear(),
      nextDueDate.getMonth() - intervalMonths,
      1,
    );

    const completedOccurrenceDue = getOccurrenceDueDate(
      previousOccurrenceMonth.getFullYear(),
      previousOccurrenceMonth.getMonth(),
      task.deadlineType || DEFAULT_TASK_DEADLINE,
    );

    const displayedOccurrenceDue = getOccurrenceDueDate(
      targetYear,
      targetMonth,
      task.deadlineType || DEFAULT_TASK_DEADLINE,
    );

    // Return true for the completed occurrence itself AND all earlier occurrences in
    // the series — those are historical and should always appear as checked.
    return displayedOccurrenceDue.getTime() <= completedOccurrenceDue.getTime();
  })();
  
  return (
    <div
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      data-task-id={task.id}
      className={`w-full min-w-0 text-left py-4 pl-2 pr-4 rounded-xl border transition-all hover:shadow-md cursor-pointer ${
        isOverdue
          ? 'bg-red-900/20 border-red-800'
          : isGardeningFilterActive
            ? 'bg-card/90 border-border/90 hover:border-primary/60'
            : 'bg-card border-border hover:border-primary/50'
      }`}
    >
      <div className="flex items-start gap-3 min-w-0">
        {/* Områdesikon och checkbox under */}
        <div className="flex-shrink-0 flex flex-col items-center gap-2">
          <div className={`p-2 rounded-lg ${area.color.split(' ')[0]}`}>
            <Icon className={`h-5 w-5 ${area.iconColor}`} />
          </div>
          {completed ? (
            <div className="rounded-lg p-2 bg-orange-500/20">
              <Check className="h-5 w-5 text-orange-500" />
            </div>
          ) : (
            <div className="rounded-lg p-2 border-2 border-muted">
              <div className="h-5 w-5" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3
                className="font-semibold text-foreground whitespace-normal break-words [overflow-wrap:anywhere] [hyphens:auto] line-clamp-2"
              >
                {task.title}
              </h3>
              {!isListView && task.description && (
                <div className="mt-1">
                  <p
                    className={`text-sm text-muted-foreground whitespace-pre-wrap break-words [overflow-wrap:anywhere] [hyphens:auto] ${isDescriptionExpanded ? '' : 'line-clamp-3'}`}
                  >
                    {task.description}
                  </p>
                  {(task.description.length > 150 || (task.description.match(/\n/g) || []).length > 2) && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setIsDescriptionExpanded(!isDescriptionExpanded);
                      }}
                      className="text-sm text-primary hover:underline mt-2 font-medium inline-block"
                    >
                      {isDescriptionExpanded ? 'Visa mindre ▲' : 'Visa mer ▼'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap min-w-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (onAreaClick) {
                  onAreaClick(task.area);
                }
              }}
              className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap border border-transparent hover:border-foreground/40 transition-colors ${area.color}`}
            >
              {area.name}
            </button>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground whitespace-nowrap">{frequencyLabels[task.frequency]}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(task.nextDue)}`}>
              {days < 0 
                ? `${Math.abs(days)} dagar sen`
                : days === 0 
                ? 'Idag!'
                : `${(() => { const [y,m,d] = task.nextDue.split('-').map(Number); return new Date(y,m-1,d).toLocaleDateString('sv-SE',{day:'numeric',month:'short'}); })()} · Om ${days} d`
              }
            </span>
            {task.tags && task.tags.length > 0 && task.tags.map((tag, index) => (
              <button
                key={index}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onTagClick) {
                    onTagClick(tag);
                  }
                }}
                className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20 whitespace-nowrap hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-1 self-start">
            {task.area === 'shopping' && onAddToShoppingList && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isAlreadyInShoppingList) onAddToShoppingList(task);
                }}
                disabled={isAlreadyInShoppingList}
                className={`p-1.5 rounded-lg transition-colors ${
                  isAlreadyInShoppingList
                    ? 'text-muted-foreground/30 cursor-not-allowed'
                    : 'text-muted-foreground hover:text-teal-400 hover:bg-muted cursor-pointer'
                }`}
                title={isAlreadyInShoppingList ? 'Redan i inköpslistan' : 'Lägg till i inköpslista'}
              >
                <ShoppingCart className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCopy();
              }}
              className="p-1.5 text-muted-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors cursor-pointer"
              title="Kopiera uppgift"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
      </div>
    </div>
  );
};

// Task Detail Modal
interface TaskDetailModalProps {
  task: RecurringTask;
  onClose: () => void;
  onComplete: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRewind: () => Promise<void>;
  getDaysUntil: (date: string) => number;
  onShowPremiumModal: () => void;
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ task, onClose, onComplete, onEdit, onDelete, onRewind, getDaysUntil, onShowPremiumModal }) => {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const family = useFamilyStore((s) => s.family);
  const hasPremiumAccess = user?.subscriptionTier === 'premium' || !!family;
  
  const area = areaConfig[task.area];
  const Icon = area.icon;
  const days = getDaysUntil(task.nextDue);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [hasRewound, setHasRewound] = useState(false);
  const [isRewinding, setIsRewinding] = useState(false);
  
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      setIsDescriptionExpanded(true);
    }
  }, []);
  
  // Disable "Markera klar" if next due date is more than 45 days in the future
  const isTooFarInFuture = days > 45;
  
  return (
    <div className="fixed bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
      <div className="bg-card rounded-2xl w-full max-w-2xl md:max-w-3xl shadow-xl border border-border max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className={`p-3 rounded-xl ${area.color.split(' ')[0]}`}>
              <Icon className={`h-8 w-8 ${area.iconColor}`} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-foreground">{task.title}</h2>
              <span className={`inline-block mt-1 px-2 py-1 rounded-full text-xs font-medium ${area.color}`}>
                {area.name}
              </span>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {task.description && (
            <div className="mb-4">
              <p
                className={`text-muted-foreground whitespace-pre-wrap break-words [overflow-wrap:anywhere] [hyphens:auto] ${isDescriptionExpanded ? '' : 'line-clamp-3'}`}
              >
                {task.description}
              </p>
              {(task.description.length > 150 || (task.description.match(/\n/g) || []).length > 2) && (
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    setIsDescriptionExpanded(!isDescriptionExpanded);
                  }}
                  className="text-sm text-primary hover:underline mt-2 font-medium inline-block"
                >
                  {isDescriptionExpanded ? 'Visa mindre ▲' : 'Visa mer ▼'}
                </button>
              )}
            </div>
          )}
          
          <div className="bg-muted rounded-xl p-4 space-y-2 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('yearWheel.frequency', 'Frekvens')}:</span>
              <span className="font-medium text-foreground">{frequencyLabels[task.frequency]}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Startmånad:</span>
              <span className="font-medium text-foreground">{months[task.month]}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Förfallodatum:</span>
              <span className={`font-medium ${days < 0 ? 'text-red-400' : 'text-foreground'}`}>
                {(() => {
                  const [y, m, d] = task.nextDue.split('-').map(Number);
                  return new Date(y, m - 1, d).toLocaleDateString('sv-SE', { day: 'numeric', month: 'long', year: 'numeric' });
                })()}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Påminnelse:</span>
              <span className="font-medium text-foreground">
                {(() => {
                  const [y, m, d] = task.nextDue.split('-').map(Number);
                  const reminder = new Date(y, m - 1, d - 7);
                  return reminder.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' }) + ' (7 dagar innan)';
                })()}
              </span>
            </div>
            {task.lastCompleted && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('yearWheel.lastCompleted', 'Senast utförd')}:</span>
                <span className="font-medium text-foreground">
                  {new Date(task.lastCompleted).toLocaleDateString('sv-SE')}
                </span>
              </div>
            )}
          </div>
          
          {days < 0 && (
            <div className="bg-red-900/20 border border-red-800 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
                <AlertTriangle className="h-4 w-4" />
                <span>{t('yearWheel.overdueWarning', 'Denna uppgift är försenad med')} {Math.abs(days)} {t('yearWheel.days', 'dagar')}</span>
              </div>
            </div>
          )}
          
          {!showDeleteConfirm ? (
            <div className="space-y-3">
              <div className="flex gap-3">
                {hasPremiumAccess && (
                  <button
                    onClick={onEdit}
                    className="flex-1 px-4 py-3 border border-border text-foreground rounded-xl font-medium hover:bg-muted transition-colors"
                  >
                    Redigera
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (hasRewound || isRewinding) return;
                    setIsRewinding(true);
                    await onRewind();
                    setHasRewound(true);
                    setIsRewinding(false);
                  }}
                  disabled={hasRewound || isRewinding}
                  title={hasRewound ? 'Kan bara ångra ett steg' : 'Ångra – flytta Nästa gång ett steg bakåt'}
                  className={`px-3 py-3 rounded-xl font-medium transition-colors flex items-center justify-center ${
                    hasRewound || isRewinding
                      ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
                      : 'border border-border text-foreground hover:bg-muted'
                  }`}
                >
                  <RotateCcw className={`h-4 w-4 ${isRewinding ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={onComplete}
                  disabled={isTooFarInFuture}
                  className={`flex-1 px-4 py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                    isTooFarInFuture 
                      ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-50' 
                      : 'btn-primary-custom'
                  }`}
                  title={isTooFarInFuture ? `Kan markeras klar ${45 - days} dagar innan` : ''}
                >
                  {t('yearWheel.markComplete', 'Markera klar')}
                </button>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (!hasPremiumAccess) {
                      onShowPremiumModal();
                      return;
                    }
                    setShowDeleteConfirm(true);
                  }}
                  className="flex-1 px-4 py-2 border border-red-800 text-red-400 rounded-xl font-medium hover:bg-red-900/20 transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <Trash2 className="h-4 w-4" /> Radera
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-xl font-medium hover:bg-muted/80 transition-colors text-sm"
                >
                  {t('common.close', 'Stäng')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-red-900/20 border border-red-800 rounded-xl p-3">
                <p className="text-red-400 text-sm font-medium">
                  Är du säker på att du vill radera denna uppgift?
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-3 bg-muted text-muted-foreground rounded-xl font-medium hover:bg-muted/80 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={() => {
                    onDelete();
                    onClose();
                  }}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors"
                >
                  Radera
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Add Task Modal
interface AddTaskModalProps {
  onClose: () => void;
  onAdd: (task: Omit<RecurringTask, 'id' | 'nextDue' | 'lastCompleted' | 'createdAt' | 'updatedAt'>) => void;
  initialMonth?: number;
  isNewTask?: boolean;
  initialData?: RecurringTask;
  availableTags: string[];
  initialArea?: PrepArea;
}

const AddTaskModal: React.FC<AddTaskModalProps> = ({ onClose, onAdd, initialMonth, initialData, availableTags, initialArea }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initialData?.title ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [area, setArea] = useState<PrepArea>(initialData?.area ?? initialArea ?? 'water');
  const [frequency, setFrequency] = useState<TaskFrequency>(initialData?.frequency ?? 'quarterly');
  const [month, setMonth] = useState<number>(initialData?.month ?? initialMonth ?? new Date().getMonth());
  const [deadlineType, setDeadlineType] = useState<TaskDeadline>(initialData?.deadlineType ?? DEFAULT_TASK_DEADLINE);
  const [tags, setTags] = useState<string[]>(initialData?.tags ?? []);
  const [tagInput, setTagInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionSearch, setSuggestionSearch] = useState('');
  const [showTagPicker, setShowTagPicker] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    
    await onAdd({
      title: title.trim(),
      description: description.trim(),
      area,
      frequency,
      month,
      deadlineType,
      notifyDaysBefore: 7,
      isCustom: true,
      enabled: true,
      tags,
    });
    
    onClose();
  };
  
  return (
    <div className="fixed bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
      <div className="bg-card rounded-2xl w-full max-w-2xl md:max-w-3xl shadow-xl max-h-[90vh] overflow-y-auto border border-border">
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">
              {initialData 
                ? 'Kopiera uppgift'
                : t('yearWheel.addTaskTitle', 'Lägg till uppgift')
              }
            </h2>
            <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('yearWheel.taskTitle', 'Titel')} *
                <span className="ml-2 text-xs text-muted-foreground">{title.length}/50</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  maxLength={50}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="flex-1 px-4 py-2 border border-border bg-input text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="T.ex. Kontrollera brandvarnare"
                  required
                />
                <button
                  type="button"
                  onClick={() => {
                    setSuggestionSearch('');
                    setShowSuggestions(true);
                  }}
                  className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  title="Visa förslag"
                >
                  <Search className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('yearWheel.taskDescription', 'Beskrivning')}
                <span className="ml-2 text-xs text-muted-foreground">{description.length}/2000</span>
              </label>
              <textarea
                maxLength={2000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-2 border border-border bg-input text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                rows={6}
                placeholder="Valfri beskrivning..."
              />
            </div>
            
            <AreaDropdown
              value={area}
              onChange={setArea}
              label={t('yearWheel.taskArea', 'Område')}
            />
            
            <FrequencyDropdown
              value={frequency}
              onChange={setFrequency}
              label={t('yearWheel.taskFrequency', 'Frekvens')}
            />
            
            <MonthDropdown
              value={month}
              onChange={setMonth}
              label="Startmånad"
            />

            <DeadlineDropdown
              value={deadlineType}
              onChange={setDeadlineType}
              label="Förfallodatum"
            />
            <p className="text-xs text-muted-foreground -mt-2">
              Välj vilken dag i månaden uppgiften ska vara klar.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Taggar (max 5)
              </label>
              <div className="space-y-2">
                {availableTags.length > 0 && tags.length < 5 && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowTagPicker((prev) => !prev)}
                      className="w-full px-3 py-2 border border-border bg-input text-foreground text-sm rounded-lg flex items-center justify-between gap-2 hover:bg-muted transition-colors"
                    >
                      <span className="truncate text-left">
                        Välj befintlig tagg...
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 flex-shrink-0 transition-transform ${showTagPicker ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {showTagPicker && (
                      <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-card shadow-lg max-h-60 overflow-y-auto">
                        {availableTags
                          .filter(tag => !tags.includes(tag))
                          .map(tag => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => {
                                if (!tags.includes(tag) && tags.length < 5) {
                                  setTags([...tags, tag]);
                                }
                                setShowTagPicker(false);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
                            >
                              {tag}
                            </button>
                          ))}
                        {availableTags.filter(tag => !tags.includes(tag)).length === 0 && (
                          <div className="px-3 py-2 text-xs text-muted-foreground italic">
                            Inga fler taggar att välja
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-primary/10 text-primary border border-primary/20"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => setTags(tags.filter((_, i) => i !== index))}
                          className="hover:text-primary/70"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {tags.length < 5 && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const trimmed = tagInput.trim();
                          if (trimmed && tags.length < 5 && !tags.includes(trimmed)) {
                            setTags([...tags, trimmed]);
                            setTagInput('');
                          }
                        }
                      }}
                      maxLength={20}
                      className="flex-1 px-4 py-2 border border-border bg-input text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Skriv en tagg och tryck Enter"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const trimmed = tagInput.trim();
                        if (trimmed && tags.length < 5 && !tags.includes(trimmed)) {
                          setTags([...tags, trimmed]);
                          setTagInput('');
                        }
                      }}
                      disabled={!tagInput.trim() || tags.length >= 5}
                      className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      style={{ backgroundColor: '#67724e' }}
                      onMouseEnter={(e) => !tagInput.trim() || tags.length >= 5 ? null : e.currentTarget.style.backgroundColor = '#5a6343'}
                      onMouseLeave={(e) => !tagInput.trim() || tags.length >= 5 ? null : e.currentTarget.style.backgroundColor = '#67724e'}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              disabled={!title.trim()}
              className="flex-1 px-4 py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-white"
              style={{ backgroundColor: '#67724e' }}
              onMouseEnter={(e) => !title.trim() ? null : e.currentTarget.style.backgroundColor = '#5a6343'}
              onMouseLeave={(e) => !title.trim() ? null : e.currentTarget.style.backgroundColor = '#67724e'}
            >
              <Plus className="h-4 w-4" /> {t('yearWheel.addTask', 'Lägg till')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-muted text-muted-foreground rounded-xl font-medium hover:bg-muted/80 transition-colors"
            >
              {t('common.cancel', 'Avbryt')}
            </button>
          </div>
        </form>
      </div>

      {/* Task Suggestions Modal */}
      {showSuggestions && (
        <TaskSuggestionsModal
          search={suggestionSearch}
          onSearchChange={setSuggestionSearch}
          onSelect={(suggestion) => {
            setTitle(suggestion.title);
            setDescription(suggestion.description);
            setArea(suggestion.area);
            setFrequency(suggestion.frequency);
            setMonth(suggestion.month);
            setShowSuggestions(false);
          }}
          onClose={() => setShowSuggestions(false)}
        />
      )}
    </div>
  );
};

// Edit Task Modal
interface EditTaskModalProps {
  task: RecurringTask;
  onClose: () => void;
  onSave: (updates: Partial<RecurringTask>) => void;
  availableTags: string[];
}

const EditTaskModal: React.FC<EditTaskModalProps> = ({ task, onClose, onSave, availableTags }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const [area, setArea] = useState<PrepArea>(task.area);
  const [frequency, setFrequency] = useState<TaskFrequency>(task.frequency);
  const [month, setMonth] = useState<number>(task.month);
  const [deadlineType, setDeadlineType] = useState<TaskDeadline>(task.deadlineType ?? DEFAULT_TASK_DEADLINE);
  const [tags, setTags] = useState<string[]>(task.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [showTagPicker, setShowTagPicker] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    
    await onSave({
      title: title.trim(),
      description: description.trim(),
      area,
      frequency,
      month,
      deadlineType,
      tags,
    });
  };
  
  return (
    <div className="fixed bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
      <div className="bg-card rounded-2xl w-full max-w-2xl md:max-w-3xl shadow-xl max-h-[90vh] overflow-y-auto border border-border my-8">
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">
              Redigera uppgift
            </h2>
            <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('yearWheel.taskTitle', 'Titel')} *
                <span className="ml-2 text-xs text-muted-foreground">{title.length}/50</span>
              </label>
              <input
                type="text"
                maxLength={50}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 border border-border bg-input text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="T.ex. Kontrollera brandvarnare"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('yearWheel.taskDescription', 'Beskrivning')}
                <span className="ml-2 text-xs text-muted-foreground">{description.length}/2000</span>
              </label>
              <textarea
                maxLength={2000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-2 border border-border bg-input text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                rows={6}
                placeholder="Valfri beskrivning..."
              />
            </div>
            
            <AreaDropdown
              value={area}
              onChange={setArea}
              label={t('yearWheel.taskArea', 'Område')}
            />
            
            <FrequencyDropdown
              value={frequency}
              onChange={setFrequency}
              label={t('yearWheel.taskFrequency', 'Frekvens')}
            />
            
            <MonthDropdown
              value={month}
              onChange={setMonth}
              label="Startmånad"
            />

            <DeadlineDropdown
              value={deadlineType}
              onChange={setDeadlineType}
              label="Förfallodatum"
            />
            <p className="text-xs text-muted-foreground -mt-2">
              Välj vilken dag i månaden uppgiften ska vara klar.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Taggar (max 5)
              </label>
              <div className="space-y-2">
                {availableTags.length > 0 && tags.length < 5 && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowTagPicker((prev) => !prev)}
                      className="w-full px-3 py-2 border border-border bg-input text-foreground text-sm rounded-lg flex items-center justify-between gap-2 hover:bg-muted transition-colors"
                    >
                      <span className="truncate text-left">
                        Välj befintlig tagg...
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 flex-shrink-0 transition-transform ${showTagPicker ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {showTagPicker && (
                      <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-card shadow-lg max-h-60 overflow-y-auto">
                        {availableTags
                          .filter(tag => !tags.includes(tag))
                          .map(tag => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => {
                                if (!tags.includes(tag) && tags.length < 5) {
                                  setTags([...tags, tag]);
                                }
                                setShowTagPicker(false);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
                            >
                              {tag}
                            </button>
                          ))}
                        {availableTags.filter(tag => !tags.includes(tag)).length === 0 && (
                          <div className="px-3 py-2 text-xs text-muted-foreground italic">
                            Inga fler taggar att välja
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-primary/10 text-primary border border-primary/20"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => setTags(tags.filter((_, i) => i !== index))}
                          className="hover:text-primary/70"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {tags.length < 5 && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const trimmed = tagInput.trim();
                          if (trimmed && tags.length < 5 && !tags.includes(trimmed)) {
                            setTags([...tags, trimmed]);
                            setTagInput('');
                          }
                        }
                      }}
                      maxLength={20}
                      className="flex-1 px-4 py-2 border border-border bg-input text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Skriv en tagg och tryck Enter"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const trimmed = tagInput.trim();
                        if (trimmed && tags.length < 5 && !tags.includes(trimmed)) {
                          setTags([...tags, trimmed]);
                          setTagInput('');
                        }
                      }}
                      disabled={!tagInput.trim() || tags.length >= 5}
                      className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      style={{ backgroundColor: '#67724e' }}
                      onMouseEnter={(e) => !tagInput.trim() || tags.length >= 5 ? null : e.currentTarget.style.backgroundColor = '#5a6343'}
                      onMouseLeave={(e) => !tagInput.trim() || tags.length >= 5 ? null : e.currentTarget.style.backgroundColor = '#67724e'}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            
          </div>
          {/* Uppgifter är alltid aktiva; ingen checkbox i edit-modal */}
          
          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              disabled={!title.trim()}
              className="flex-1 px-4 py-3 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: title.trim() ? '#67724e' : undefined }}
              onMouseEnter={(e) => { if (title.trim()) e.currentTarget.style.backgroundColor = '#5a6343'; }}
              onMouseLeave={(e) => { if (title.trim()) e.currentTarget.style.backgroundColor = '#67724e'; }}
            >
              <Check className="h-4 w-4" /> Spara
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-muted text-muted-foreground rounded-xl font-medium hover:bg-muted/80 transition-colors"
            >
              {t('common.cancel', 'Avbryt')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Task Suggestions Modal
interface TaskSuggestionsModalProps {
  search: string;
  onSearchChange: (value: string) => void;
  onSelect: (suggestion: TaskSuggestion) => void;
  onClose: () => void;
}

const TaskSuggestionsModal: React.FC<TaskSuggestionsModalProps> = ({ search, onSearchChange, onSelect, onClose }) => {
  const filteredSuggestions = useMemo(() => {
    const searchLower = search.toLowerCase();
    return TASK_SUGGESTIONS.filter(s => 
      s.title.toLowerCase().includes(searchLower) || 
      s.description.toLowerCase().includes(searchLower) ||
      areaConfig[s.area].name.toLowerCase().includes(searchLower)
    );
  }, [search]);

  return (
    <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
      <div className="w-full max-w-2xl rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Välj uppgift från förslag</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="mb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Sök uppgift..."
              className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
            />
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto space-y-2">
          {filteredSuggestions.map((suggestion, idx) => {
            const area = areaConfig[suggestion.area];
            const Icon = area.icon;
            return (
              <button
                key={idx}
                onClick={() => onSelect(suggestion)}
                className="w-full text-left p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${area.color.split(' ')[0]} shrink-0`}>
                    <Icon className={`h-5 w-5 ${area.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        {suggestion.title}
                      </h4>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {months[suggestion.month]}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {suggestion.description}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${area.color}`}>
                        {area.name}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        {frequencyLabels[suggestion.frequency]}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
          {filteredSuggestions.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              Inga förslag hittades
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default YearWheelPage;
